import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { sendShipmentUpdateEmail } from '../../utils/emailService';
import AdminShell from '../Layout/AdminShell';
import { matchesShipmentSearch } from '../../utils/shipmentSearch';
import {
  PageHead, Panel, Pill, btn,
} from '../common/AdminUI';
import ShipmentsTable from '../common/ShipmentsTable';
import { mockShipments, mockUsers } from '../../utils/mockData';
import { Search, Upload, Eye, X, Save, FileText, Trash2, Plane, Ship, Download, Inbox, Package } from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS, apiCall, getAuthHeaders } from '../../config/api';
import { uploadEmployeeShipmentPDFs, uploadSeaExportShipmentPDFs, getCloudFrontUrl } from '../../utils/s3Upload';
import SeaExportForm from './SeaExportForm';
import SeaExportTable from './SeaExportTable';
import PendingApprovalQueue from './PendingApprovalQueue';
import { escapeCsv } from '../../utils/exportCsv';

const EmployeeDashboard = () => {
  const { allUsers } = useAuth();
  const [activeTab, setActiveTab] = useState('Air-Export');
  const [consoleSearch, setConsoleSearch] = useState('');
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);

  // Lightweight, independent of PendingApprovalQueue mounting — so the tab badge
  // shows a count immediately on load, not only after the user opens that view.
  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall(`${API_ENDPOINTS.GET_ALL_SHIPMENTS}?approvalStatus=PENDING_APPROVAL&limit=1&offset=0`, { method: 'GET' });
        const result = await res.json();
        if (res.ok && result.success) setPendingApprovalCount(result.data?.totalRecords || 0);
      } catch {
        // badge count is a convenience — fail silently
      }
    })();
  }, []);
  const [shipments, setShipments] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [fetchForm, setFetchForm] = useState({
    customer: '',
    selectBox2: '',
    awbNumber: ''
  });
  const [fetchError, setFetchError] = useState('');
  const [duplicateAwbMessage, setDuplicateAwbMessage] = useState('');
  const [duplicateAwbShipment, setDuplicateAwbShipment] = useState(null);
  const [fetchedData, setFetchedData] = useState([]);
  const [editingShipment, setEditingShipment] = useState(null);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [shipmentToDelete, setShipmentToDelete] = useState(null);
  const [showPdfDeleteModal, setShowPdfDeleteModal] = useState(false);
  const [pdfToDelete, setPdfToDelete] = useState(null);
  const [submitFormData, setSubmitFormData] = useState({
    customer_name: '',
    DBK: '0',
    Rodtep: '0',
    Rosctl: '0',
    IGST: '0',
    awb: null,
    leo: [],
        inv: [], // This line is unchanged
  });
  const [scrapedDataForSubmit, setScrapedDataForSubmit] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  
  // LEO PDF data fetching states
  const [fetchedLEOData, setFetchedLEOData] = useState(null);
  const [fetchedLEORecords, setFetchedLEORecords] = useState([]);
  const [isLoadingLEOData, setIsLoadingLEOData] = useState(false);
  const [leoFetchError, setLeoFetchError] = useState('');
  
  // AWB PDF validation states
  const [isValidatingAwbPdf, setIsValidatingAwbPdf] = useState(false);
  const [awbPdfError, setAwbPdfError] = useState('');
  
  // Sea Export specific states
  const [seaExportBookings, setSeaExportBookings] = useState([]);
  const [loadingSeaExport, setLoadingSeaExport] = useState(false);
  const [seaSelectedCustomerId, setSeaSelectedCustomerId] = useState('');
  const [isFetchingData, setIsFetchingData] = useState(false);
  
  // Submit form loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalizeSeaContainerNoDisplay = (value) => {
    if (value === null || value === undefined) return '';

    const isValid = (v) => {
      const t = String(v || '').trim();
      return t !== '' && t !== '-';
    };

    if (Array.isArray(value)) {
      const cleaned = value.map(v => String(v || '').trim()).filter(isValid);
      return cleaned.join(' & ');
    }

    const raw = String(value).trim();
    if (!raw || raw === '-' || raw === '[]' || raw === '[""]') return '';

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map(v => String(v || '').trim()).filter(isValid).join(' & ');
      }
    } catch {
      // fallback below
    }

    if (raw.includes(' & ') || raw.includes(',')) {
      return raw
        .split(/\s*&\s*|\s*,\s*/)
        .map(v => v.trim())
        .filter(isValid)
        .join(' & ');
    }

    return isValid(raw) ? raw : '';
  };

  // Fetch customers from API
  useEffect(() => {
    const fetchCustomers = async () => {
      setLoadingCustomers(true);
      
      try {
        const response = await apiCall(API_ENDPOINTS.GET_CUSTOMERS, {
          method: 'GET'
        });

        const data = await response.json();

        if (response.ok && response.status === 200) {
          console.log('Customers API Response:', data);
          console.log('Raw data array:', data.data);
          
          let rawCustomers = data.data || [];
          if (!Array.isArray(rawCustomers)) {
            console.log('rawCustomers is not array:', rawCustomers);
            rawCustomers = [];
          }
          
          const customersList = rawCustomers.map(c => ({
            id: c.id,
            name: c.our_users || c.custName || c.name || c.username || 'Unknown'
          }));
          console.log('Mapped customers list:', customersList);
          setCustomers(customersList);
        } else {
          console.error('Failed to fetch customers:', data);
          setCustomers(mockUsers.filter(user => user.role === 'user'));
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
        setCustomers(mockUsers.filter(user => user.role === 'user'));
      } finally {
        setLoadingCustomers(false);
      }
    };

    fetchCustomers();
  }, []);

  // Fetch sea export shipments from API
  useEffect(() => {
    const fetchSeaExportShipments = async () => {
      if (activeTab !== 'Sea-Export') return;
      
      setLoadingSeaExport(true);
      
      try {
        const response = await apiCall(API_ENDPOINTS.GET_SEA_EXPORT, {
          method: 'GET'
        });

        const data = await response.json();

        if (response.ok && data.success) {
          console.log('Sea Export API Response:', data);
          
          const formattedShipments = (data.data || []).map(s => ({
            id: s.id,
            portCode: s.port_code || s.portCode || '-',
            sbNo: s.sb_no || s.sbNo || '-',
            sbDate: s.sb_date || s.sbDate || '-',
            pkg: s.pkg || '-',
            gwt: s.gwt || '-',
            portOfLoading: s.port_of_loading || s.portOfLoading || '-',
            stateOfOrigin: s.state_of_origin || s.stateOfOrigin || '-',
            portOfDischargeCode: s.port_of_discharge_code || s.portOfDischargeCode || '-',
            portOfDischarge: s.port_of_discharge || s.portOfDischarge || '-',
            portOfFinalDestinationCode: s.port_of_final_destination_code || s.portOfFinalDestinationCode || '-',
            portOfFinalDestination: s.port_of_final_destination || s.portOfFinalDestination || '-',
            countryOfFinalDestination: s.country_of_final_destination || s.countryOfFinalDestination || '-',
            exporterName: s.exporter_name || s.exporterName || '-',
            exporterAddress: s.exporter_address || s.exporterAddress || '-',
            gstNo: s.gst_no || s.gstNo || '-',
            buyerName: s.buyer_name || s.buyerName || '-',
            consigneeName: s.consignee_name || s.consigneeName || '-',
            mode: s.mode || '-',
            lut: s.lut || '-',
            fobValue: s.fob_value || s.fobValue || '-',
            dbkClaim: s.dbk_claim || s.dbkClaim || '-',
            igstAmt: s.igst_amt || s.igstAmt || '-',
            cessAmt: s.cess_amt || s.cessAmt || '-',
            igstValue: s.igst_value || s.igstValue || '-',
            rodtepAmt: s.rodtep_amt || s.rodtepAmt || '-',
            rosctlAmt: s.rosctl_amt || s.rosctlAmt || '-',
            invNo: s.inv_no || s.invNo || '-',
            invAmt: s.inv_amt || s.invAmt || '-',
            currency: s.currency || '-',
            leoDate: s.leo_date || s.leoDate || '-',
            invoiceNoDt: s.invoice_no_dt || s.invoiceNoDt || '-',
            blNumber: s.bl_number || s.blNumber || '-',
            containerNo: normalizeSeaContainerNoDisplay(s.container_no || s.containerNo),
            containerQty: s.container_qty || s.containerQty || '-',
            leoPdf: s.leo_pdf || s.leoPdf || '',
            blPdf: s.bl_pdf || s.blPdf || '',
            invPlPdf: s.inv_pl_pdf || s.invPlPdf || '',
            invoicePdf: s.invoice_pdf || s.invoicePdf || '',
            purPdf: s.pur_pdf || s.purPdf || '',
            customerId: s.user_id || s.userId || null,
            editedBy: s.edited_by || s.editedBy || '-',
            createdAt: s.created_at || s.createdAt || new Date().toISOString(),
            type: 'Sea-Export'
          }));
          
          setSeaExportBookings(formattedShipments);
        } else {
          console.error('Failed to fetch sea export shipments:', data);
        }
      } catch (error) {
        console.error('Error fetching sea export shipments:', error);
      } finally {
        setLoadingSeaExport(false);
      }
    };

    fetchSeaExportShipments();
  }, [activeTab]);

  // Fetch all shipments from API
  useEffect(() => {
    const fetchShipments = async () => {
      try {
        const response = await apiCall(API_ENDPOINTS.GET_ALL_SHIPMENTS, {
          method: 'GET'
        });

        const data = await response.json();

        if (response.ok && data.success) {
          // Shipments created by Gmail automation sit in PENDING_APPROVAL/REJECTED until
          // a human approves them via the Pending Approval tab — keep them out of the
          // main list until then (manually-created records have approvalStatus === null).
          const formattedShipments = (data.data?.shipments || [])
            .filter(s => s.approvalStatus !== 'PENDING_APPROVAL' && s.approvalStatus !== 'REJECTED')
            .map(s => {
            const parseField = (field) => {
              try {
                return typeof field === 'string' ? JSON.parse(field) : field;
              } catch {
                return field;
              }
            };

            const joinArray = (arr) => {
              if (!arr) return '-';
              if (Array.isArray(arr)) {
                return arr.join(' & ');
              }
              return arr;
            };

            const formatDates = (dates) => {
              if (!dates || !Array.isArray(dates) || dates.length === 0) {
                return '-';
              }

              const filtered = dates.filter(date => date);

              if (filtered.length === 0) {
                return '-';
              }

              const formattedDates = filtered.map(date => {
                if (!date) return null;
                let dateStr = date;
                if (typeof date === 'string' && date.includes(' ')) {
                  dateStr = date.split(' ')[0];
                }
                
                const ddmmyyyyPattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
                const match = dateStr.match(ddmmyyyyPattern);
                if (match) {
                  return dateStr;
                }
                
                try {
                  const dateObj = new Date(dateStr);
                  if (!isNaN(dateObj.getTime())) {
                    let day = dateObj.getDate();
                    let month = dateObj.getMonth() + 1;
                    let year = dateObj.getFullYear();
                    if (year < 100) {
                      year += 2000;
                    }
                    return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
                  }
                } catch (e) {
                  console.error('Date parsing error:', e);
                }
                return null;
              }).filter(date => date !== null);

              const unique = [...new Set(formattedDates)];
              return unique.join(' & ');
            };

            const getUniqueFlights = (flightNums, flightDts) => {
              if (!Array.isArray(flightNums) || !Array.isArray(flightDts)) {
                return { uniqueFlightNumbers: flightNums, uniqueFlightDates: flightDts };
              }
              
              const seen = new Set();
              const uniqueNumbers = [];
              const uniqueDates = [];
              
              for (let i = 0; i < flightNums.length; i++) {
                const combo = `${flightNums[i]}_${flightDts[i]}`;
                if (!seen.has(combo)) {
                  seen.add(combo);
                  uniqueNumbers.push(flightNums[i]);
                  uniqueDates.push(flightDts[i]);
                }
              }
              
              return { uniqueFlightNumbers: uniqueNumbers, uniqueFlightDates: uniqueDates };
            };

            const sbNumbers = parseField(s.sbNumbers);
            const sbDates = parseField(s.sbDates);
            const sbFobValues = parseField(s.sbFobValues);
            const flightNumbers = parseField(s.flightNumbers);
            const flightDates = parseField(s.flightDates);
            const paymentActivity = parseField(s.paymentActivity);
            const paymentDates = parseField(s.paymentDates);
            const paymentsAmount = parseField(s.paymentsAmount);

            const { uniqueFlightNumbers, uniqueFlightDates } = getUniqueFlights(flightNumbers, flightDates);

            return {
              ...s,
              awbNumber: s.awbNumber,
              username: s.username,
              customerName: s.customerName,
              consigneeName: s.consigneeName || s.consignee_name || '',
              sbNumber: joinArray(sbNumbers),
              sbDate: formatDates(sbDates),
              cityCode: s.destCode,
              destCity: s.destCity,
              destCountry: s.destCountry,
              flightNo: joinArray(uniqueFlightNumbers),
              flightDate: joinArray(uniqueFlightDates),
              totalPkt: s.numberOfPackages,
              weight: s.grossWeight,
              chWeight: s.chargeableWeight,
              amount: joinArray(paymentsAmount),
              activity: joinArray(paymentActivity),
              fob: joinArray(sbFobValues),
              dbk: joinArray(parseField(s.DBK ?? s.dbk ?? s.dbkClaim)),
              rodtep: joinArray(parseField(s.Rodtep ?? s.rodtep ?? s.rodtepAmt)),
              rosctl: joinArray(parseField(s.Rosctl ?? s.rosctl ?? s.rosctlAmt)),
              igst: joinArray(parseField(s.IGST ?? s.igst ?? s.igstAmt)),
              awbPdf: s.awbPdf,
              leo: s.leoPdfKey || s.leoPdf,
              inv: s.invPdf,
              customer: s.customerName,
              sbNumbers: sbNumbers,
              sbDates: sbDates,
              sbFobValues: sbFobValues,
              flightNumbers: flightNumbers,
              flightDates: flightDates,
              paymentActivity: paymentActivity,
              paymentDates: paymentDates,
              paymentsAmount: paymentsAmount,
              sbPackages: parseField(s.sbPackages),
              sbGrossWeights: parseField(s.sbGrossWeights),
              uldNumbers: parseField(s.uldNumbers),
              uldTypes: parseField(s.uldTypes),
              locations: parseField(s.locations),
              locationPieces: parseField(s.locationPieces),
              screeningInfo: parseField(s.screeningInfo)
            };
          });
          
          console.log('Formatted Shipments:', formattedShipments);
          console.log('Total Shipments:', formattedShipments.length);
          
          const uniqueShipments = formattedShipments.filter((shipment, index, self) =>
            index === self.findIndex((s) => s.awbNumber === shipment.awbNumber)
          );
          
          console.log('Unique Shipments:', uniqueShipments.length);
          setShipments(uniqueShipments);
        } else {
          console.error('Failed to fetch shipments:', data);
        }
      } catch (error) {
        console.error('Error fetching shipments:', error);
      }
    };

    fetchShipments();
  }, []);

  // Function to refetch shipments
  const refetchShipments = async () => {
    
    try {
      // Clear existing shipments first to avoid duplication
      setShipments([]);
      
      const response = await apiCall(API_ENDPOINTS.GET_ALL_SHIPMENTS, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
        });

      const data = await response.json();

      if (response.ok && data.success) {
        // Check if data.data exists and is an array
        const rawShipmentsArray = data.data?.shipments || data.data || [];
        if (!Array.isArray(rawShipmentsArray)) {
          console.log('No shipments data or invalid format');
          setShipments([]);
          return;
        }
        // Keep automation-created PENDING_APPROVAL/REJECTED shipments out of the main
        // list — they only graduate here once approved via the Pending Approval tab.
        const shipmentsArray = rawShipmentsArray.filter(s => s.approvalStatus !== 'PENDING_APPROVAL' && s.approvalStatus !== 'REJECTED');

        const parseField = (field) => {
          if (typeof field === 'string') {
            try {
              return JSON.parse(field);
            } catch {
              return [];
            }
          }
          return Array.isArray(field) ? field : [];
        };

        const joinArray = (arr) => {
          return Array.isArray(arr) && arr.length > 0 ? arr.join(' & ') : '-';
        };

        // Format dates as DD/MM/YYYY and remove duplicates
        const formatDates = (dates) => {
          if (!dates || !Array.isArray(dates) || dates.length === 0) {
            return '-';
          }

          const filtered = dates.filter(date => date);

          if (filtered.length === 0) {
            return '-';
          }

          const formattedDates = filtered.map(date => {
            if (!date) return null;
            let dateStr = date;
            // Remove time part if present
            if (typeof date === 'string' && date.includes(' ')) {
              dateStr = date.split(' ')[0];
            }
            
            // Check if already in DD/MM/YYYY format
            const ddmmyyyyPattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
            const match = dateStr.match(ddmmyyyyPattern);
            if (match) {
              return dateStr; // Already in correct format
            }
            
            // Parse and format to DD/MM/YYYY
            try {
              const dateObj = new Date(dateStr);
              if (!isNaN(dateObj.getTime())) {
                let day = dateObj.getDate();
                let month = dateObj.getMonth() + 1;
                let year = dateObj.getFullYear();
                
                // Fix for years like 025 (should be 2025)
                if (year < 100) {
                  year += 2000;
                }
                
                return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
              }
            } catch (e) {
              console.error('Date parsing error:', e);
            }
            return null;
          }).filter(date => date !== null);

          const unique = [...new Set(formattedDates)];

          return unique.join(' & ');
        };

        const getUniqueFlights = (flightNums, flightDts) => {
          if (!Array.isArray(flightNums) || !Array.isArray(flightDts)) {
            return { uniqueFlightNumbers: [], uniqueFlightDates: [] };
          }
          
          const seen = new Set();
          const uniqueNumbers = [];
          const uniqueDates = [];
          
          for (let i = 0; i < flightNums.length; i++) {
            const combo = `${flightNums[i]}_${flightDts[i]}`;
            if (!seen.has(combo)) {
              seen.add(combo);
              uniqueNumbers.push(flightNums[i]);
              uniqueDates.push(flightDts[i]);
            }
          }
          
          return { uniqueFlightNumbers: uniqueNumbers, uniqueFlightDates: uniqueDates };
        };

        const formattedShipments = shipmentsArray.map(s => {
          const sbNumbers = parseField(s.sbNumbers);
          const sbDates = parseField(s.sbDates);
          const sbFobValues = parseField(s.sbFobValues);
          const flightNumbers = parseField(s.flightNumbers);
          const flightDates = parseField(s.flightDates);
          const paymentActivity = parseField(s.paymentActivity);
          const paymentDates = parseField(s.paymentDates);
          const paymentsAmount = parseField(s.paymentsAmount);

          const { uniqueFlightNumbers, uniqueFlightDates } = getUniqueFlights(flightNumbers, flightDates);

          return {
            ...s,
            awbNumber: s.awbNumber,
            username: s.username,
            customerName: s.customerName,
            consigneeName: s.consigneeName || s.consignee_name || '',
            sbNumber: joinArray(sbNumbers),
            sbDate: formatDates(sbDates),
            cityCode: s.destCode,
            destCity: s.destCity,
            destCountry: s.destCountry,
            flightNo: joinArray(uniqueFlightNumbers),
            flightDate: joinArray(uniqueFlightDates),
            totalPkt: s.numberOfPackages,
            weight: s.grossWeight,
            chWeight: s.chargeableWeight,
            amount: joinArray(paymentsAmount),
            activity: joinArray(paymentActivity),
            fob: joinArray(sbFobValues),
            dbk: s.DBK,
            rodtep: s.Rodtep,
            rosctl: s.Rosctl,
            igst: s.IGST,
            awbPdf: s.awbPdf,
            leo: s.leoPdfKey || s.leoPdf,
            inv: s.invPdf,
            customer: s.customerName,
            sbNumbers: sbNumbers,
            sbDates: sbDates,
            sbFobValues: sbFobValues,
            flightNumbers: flightNumbers,
            flightDates: flightDates,
            paymentActivity: paymentActivity,
            paymentDates: paymentDates,
            paymentsAmount: paymentsAmount,
            sbPackages: parseField(s.sbPackages),
            sbGrossWeights: parseField(s.sbGrossWeights),
            uldNumbers: parseField(s.uldNumbers),
            uldTypes: parseField(s.uldTypes),
            locations: parseField(s.locations),
            locationPieces: parseField(s.locationPieces),
            screeningInfo: parseField(s.screeningInfo)
          };
        });
        
        // Remove duplicates by AWB number
        const uniqueShipments = formattedShipments.filter((shipment, index, self) =>
          index === self.findIndex((s) => s.awbNumber === shipment.awbNumber)
        );
        
        console.log('Refetch - Total:', formattedShipments.length, 'Unique:', uniqueShipments.length);
        setShipments(uniqueShipments);
      }
    } catch (error) {
      console.error('Error refetching shipments:', error);
    }
  };

  // Function to parse and clean scraped data
  const parseScrapedData = (scrapedData) => {
    const cleanedData = {
      awbNumber: scrapedData.awb_number || '',
      serviceName: scrapedData.service_name || '',
      serviceCode: scrapedData.service_code || '',
      extractionTime: scrapedData.extraction_time || '',
      totalTables: scrapedData.total_tables || 0,
      awbDetails: {},
      statusDetails: [],
      paymentDetails: [],
      shippingBillDetails: [],
      loadingDetails: [],
      locationDetails: [],
      screeningDetails: []
    };

    const isGMR = scrapedData.service_name?.includes('GMR');
    const isDCSC = scrapedData.service_name?.includes('DCSC');

    console.log('Parsing data for service:', scrapedData.service_name, 'isGMR:', isGMR, 'isDCSC:', isDCSC);

    // Parse tables if available
    if (scrapedData.tables && scrapedData.tables.length > 0) {
      scrapedData.tables.forEach(table => {
        const tableId = table.table_id || '';
        const rows = table.rows || [];
        
        console.log(`Processing table: ${tableId} with ${rows.length} rows`);

        // GMR specific parsing
        if (isGMR) {
          // GridConsignmentDetails - AWB details with flight info
          if (tableId === 'GridConsignmentDetails' && rows.length > 1) {
            const dataRow = rows[1];
            if (dataRow && dataRow.length >= 11) {
              cleanedData.awbDetails = {
                destination: dataRow[0] || '',
                numberOfPackages: dataRow[1] || '',
                grossWeight: dataRow[2] || '',
                chargeableWeight: dataRow[3] || '',
                volWeight: dataRow[4] || '',
                tdgDate: dataRow[5] || '',
                bagDate: dataRow[6] || '',
                withdrawalDate: dataRow[7] || '',
                leoStatus: dataRow[8] || '',
                flightNo: dataRow[9] || '',
                flightDate: dataRow[10] || ''
              };
              console.log('GMR AWB Details parsed:', cleanedData.awbDetails);
            }
          }
          
          // GridPaymentDetails
          else if (tableId === 'GridPaymentDetails') {
            for (let i = 1; i < rows.length; i++) {
              const row = rows[i];
              if (row.length >= 4) {
                cleanedData.paymentDetails.push({
                  tcNo: row[0] || '',
                  tcDate: row[1] || '',
                  amount: row[2] || '',
                  activity: row[3] || ''
                });
              }
            }
            console.log('GMR Payment Details:', cleanedData.paymentDetails.length);
          }
          
          // GridShippingBillDetails
          else if (tableId === 'GridShippingBillDetails') {
            for (let i = 1; i < rows.length; i++) {
              const row = rows[i];
              if (row.length >= 6) {
                cleanedData.shippingBillDetails.push({
                  sbNo: row[0] || '',
                  sbDate: row[1] || '',
                  hawbNo: row[2] || '',
                  packages: row[3] || '',
                  grossWeight: row[4] || '',
                  fobValue: row[5] || ''
                });
              }
            }
            console.log('GMR Shipping Bills:', cleanedData.shippingBillDetails.length);
          }
          
          // GridLoadingDetails - Note: columns are shifted, no Status header
          else if (tableId === 'GridLoadingDetails') {
            for (let i = 1; i < rows.length; i++) {
              const row = rows[i];
              if (row.length >= 4) {
                cleanedData.loadingDetails.push({
                  uldNo: row[0] || '',
                  uldType: row[1] || '',
                  flightNo: row[2] || '',
                  flightDate: row[3] || '',
                  flightStatus: '' // Not available in GMR
                });
              }
            }
            console.log('GMR Loading Details:', cleanedData.loadingDetails.length);
          }
          
          // grdLocation
          else if (tableId === 'grdLocation') {
            for (let i = 1; i < rows.length; i++) {
              const row = rows[i];
              if (row.length >= 2) {
                cleanedData.locationDetails.push({
                  pieces: row[0] || '',
                  location: row[1] || ''
                });
              }
            }
            console.log('GMR Location Details:', cleanedData.locationDetails.length);
          }
          
          // grdScreen
          else if (tableId === 'grdScreen') {
            for (let i = 1; i < rows.length; i++) {
              const row = rows[i];
              if (row.length >= 3) {
                cleanedData.screeningDetails.push({
                  screeningDate: row[0] || '',
                  machine: row[1] || '',
                  screenedBy: row[2] || ''
                });
              }
            }
            console.log('GMR Screening Details:', cleanedData.screeningDetails.length);
          }
        }
        
        // DCSC specific parsing
        else if (isDCSC) {
          // Find and parse different sections in DCSC format
          for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const cellText = (row[0] || '').toString();
            
            // AWB DETAILS section - look for header row
            if (cellText.includes('AWB number') && row.length > 5) {
              const nextRow = rows[i + 1];
              if (nextRow && nextRow.length >= 7) {
                cleanedData.awbDetails = {
                  awbNumber: nextRow[0] || '',
                  destination: nextRow[1] || '',
                  numberOfPackages: nextRow[2] || '',
                  grossWeight: nextRow[3] || '',
                  chargeableWeight: nextRow[4] || '',
                  withdrawalDate: nextRow[5] || '',
                  currentStatus: nextRow[6] || ''
                };
                console.log('DCSC AWB Details parsed:', cleanedData.awbDetails);
                i++; // Skip next row
              }
            }
            
            // AWB STATUS DETAILS section
            else if (cellText === 'AWB STATUS' || (row.length === 2 && cellText.includes('AWB STATUS'))) {
              let statusIndex = i + 1;
              while (statusIndex < rows.length) {
                const statusRow = rows[statusIndex];
                const statusText = (statusRow[0] || '').toString();
                
                if (statusText.includes('PAYMENT') || statusText.includes('SHIPPING') || statusText.includes('LOADING')) break;
                
                if (statusRow.length === 2 && statusText && !statusText.includes('DATE/TIME') && !statusText.includes('DETAILS')) {
                  cleanedData.statusDetails.push({
                    status: statusText.trim(),
                    dateTime: (statusRow[1] || '').toString().trim()
                  });
                }
                statusIndex++;
              }
              console.log('DCSC Status Details:', cleanedData.statusDetails.length);
              i = statusIndex - 1;
            }
            
            // PAYMENT DETAILS section
            else if (cellText === 'TC-No' || (row.length >= 4 && cellText.includes('TC-No'))) {
              let paymentIndex = i + 1;
              while (paymentIndex < rows.length) {
                const paymentRow = rows[paymentIndex];
                const paymentText = (paymentRow[0] || '').toString();
                
                if (paymentText.includes('SHIPPING') || paymentText.includes('LOADING') || paymentText.includes('DETAILS')) break;
                
                if (paymentRow.length >= 4 && paymentText && !paymentText.includes('TC-No')) {
                  cleanedData.paymentDetails.push({
                    tcNo: paymentText.trim(),
                    tcDate: (paymentRow[1] || '').toString().trim(),
                    amount: (paymentRow[2] || '').toString().trim(),
                    activity: (paymentRow[3] || '').toString().trim()
                  });
                }
                paymentIndex++;
              }
              console.log('DCSC Payment Details:', cleanedData.paymentDetails.length);
              i = paymentIndex - 1;
            }
            
            // SHIPPING BILL DETAILS section
            else if (cellText.includes('SB No') && row.length >= 6) {
              let sbIndex = i + 1;
              while (sbIndex < rows.length) {
                const sbRow = rows[sbIndex];
                const sbText = (sbRow[0] || '').toString();
                
                if (sbText.includes('LOADING') || sbText.includes('DETAILS') || sbText.includes('ULD')) break;
                
                if (sbRow.length >= 6 && sbText && !sbText.includes('SB No') && sbText.match(/^\d+$/)) {
                  cleanedData.shippingBillDetails.push({
                    sbNo: sbText.trim(),
                    sbDate: (sbRow[1] || '').toString().trim(),
                    hawbNo: (sbRow[2] || '').toString().trim(),
                    packages: (sbRow[3] || '').toString().trim(),
                    grossWeight: (sbRow[4] || '').toString().trim(),
                    fobValue: (sbRow[5] || '').toString().trim()
                  });
                }
                sbIndex++;
              }
              console.log('DCSC Shipping Bills:', cleanedData.shippingBillDetails.length);
              i = sbIndex - 1;
            }
            
            // LOADING DETAILS section
            else if (cellText.includes('ULD No') && row.length >= 6) {
              let loadingIndex = i + 1;
              while (loadingIndex < rows.length) {
                const loadingRow = rows[loadingIndex];
                const loadingText = (loadingRow[0] || '').toString();
                
                if (loadingText.includes('_____') || loadingText.includes('Delhi Cargo') || !loadingText) break;
                
                if (loadingRow.length >= 6 && loadingText && !loadingText.includes('ULD No')) {
                  cleanedData.loadingDetails.push({
                    uldNo: loadingText.trim(),
                    uldType: (loadingRow[1] || '').toString().trim(),
                    uldShipPcs: (loadingRow[2] || '').toString().trim(),
                    flightStatus: (loadingRow[3] || '').toString().trim(),
                    flightNo: (loadingRow[4] || '').toString().trim(),
                    flightDate: (loadingRow[5] || '').toString().trim()
                  });
                }
                loadingIndex++;
              }
              console.log('DCSC Loading Details:', cleanedData.loadingDetails.length);
              i = loadingIndex - 1;
            }
          }
        }
      });
    }

    console.log('Final cleaned data:', cleanedData);

    // Create arrays for specific fields as requested
    const simplifiedData = {
      awbNumber: cleanedData.awbNumber,
      serviceName: cleanedData.serviceName,
      extractionTime: cleanedData.extractionTime,
      destination: cleanedData.awbDetails.destination || '',
      destinationCode: cleanedData.awbDetails.currentStatus || cleanedData.awbDetails.destination || '',
      destinationCity: '',
      destinationCountry: '',
      numberOfPackages: cleanedData.awbDetails.numberOfPackages || '',
      grossWeight: cleanedData.awbDetails.grossWeight || '',
      chargeableWeight: cleanedData.awbDetails.chargeableWeight || '',
      currentStatus: cleanedData.awbDetails.currentStatus || cleanedData.awbDetails.leoStatus || '',
      leoStatus: cleanedData.awbDetails.leoStatus || '',
      tdgDate: cleanedData.awbDetails.tdgDate || '',
      bagDate: cleanedData.awbDetails.bagDate || '',
      withdrawalDate: cleanedData.awbDetails.withdrawalDate || '',
      statuses: cleanedData.statusDetails.map(s => s.status),
      statusDates: cleanedData.statusDetails.map(s => s.dateTime),
      sbNumbers: cleanedData.shippingBillDetails.map(sb => sb.sbNo),
      sbDates: cleanedData.shippingBillDetails.map(sb => sb.sbDate),
      sbPackages: cleanedData.shippingBillDetails.map(sb => sb.packages),
      sbGrossWeights: cleanedData.shippingBillDetails.map(sb => sb.grossWeight),
      sbFobValues: cleanedData.shippingBillDetails.map(sb => sb.fobValue),
      sbHawbNumbers: cleanedData.shippingBillDetails.map(sb => sb.hawbNo),
      flightNumbers: cleanedData.loadingDetails.map(ld => ld.flightNo).concat(cleanedData.awbDetails.flightNo ? [cleanedData.awbDetails.flightNo] : []),
      flightDates: cleanedData.loadingDetails.map(ld => ld.flightDate).concat(cleanedData.awbDetails.flightDate ? [cleanedData.awbDetails.flightDate] : []),
      flightStatuses: cleanedData.loadingDetails.map(ld => ld.flightStatus || ''),
      uldNumbers: cleanedData.loadingDetails.map(ld => ld.uldNo),
      uldTypes: cleanedData.loadingDetails.map(ld => ld.uldType),
      uldShipPcs: cleanedData.loadingDetails.map(ld => ld.uldShipPcs || ''),
      paymentActivities: cleanedData.paymentDetails.map(p => p.activity),
      paymentDates: cleanedData.paymentDetails.map(p => p.tcDate),
      paymentAmounts: cleanedData.paymentDetails.map(p => p.amount),
      paymentTcNumbers: cleanedData.paymentDetails.map(p => p.tcNo),
      locations: cleanedData.locationDetails.map(loc => loc.location),
      locationPieces: cleanedData.locationDetails.map(loc => loc.pieces),
      screeningInfo: cleanedData.screeningDetails,
      awbDetails: cleanedData.awbDetails,
      statusDetails: cleanedData.statusDetails,
      paymentDetails: cleanedData.paymentDetails,
      shippingBillDetails: cleanedData.shippingBillDetails,
      loadingDetails: cleanedData.loadingDetails,
      locationDetails: cleanedData.locationDetails,
      screeningDetails: cleanedData.screeningDetails
    };

    return simplifiedData;
  };

  // Filter shipments by type and selected customer
  const filteredShipments = shipments.filter(s => {
    // Map shipment type to tab type
    const shipmentTabType = s.shipmentType === 'gmr' || s.shipmentType === 'dcsc' ? 'Air-Export' : s.type;
    const matchesType = shipmentTabType === activeTab;
    const normalizeValue = (value) => String(value || '').trim().toLowerCase();
    const matchesCustomer = !selectedCustomer
      || normalizeValue(s.username) === normalizeValue(selectedCustomer)
      || normalizeValue(s.customerName) === normalizeValue(selectedCustomer);
    return matchesType && matchesCustomer && matchesShipmentSearch(s, consoleSearch);
  });

  const filteredSeaExportBookings = (seaSelectedCustomerId
    ? seaExportBookings.filter(s => String(s.customerId || '') === String(seaSelectedCustomerId))
    : seaExportBookings
  ).filter((s) => matchesShipmentSearch(s, consoleSearch));

  const handleFetchData = async (e) => {
    e.preventDefault();
    
    // If duplicate AWB detected, open edit modal directly
    if (duplicateAwbShipment) {
      // Use handleEditShipment to properly populate all data
      handleEditShipment(duplicateAwbShipment);
      
      // Clear the duplicate message and reset fetch form
      setDuplicateAwbMessage('');
      setDuplicateAwbShipment(null);
      setFetchForm({
        customer: '',
        selectBox2: '',
        awbNumber: ''
      });
      return;
    }
    
    // Check if AWB number already exists (should not happen due to onChange check)
    const existingShipment = shipments.find(s => s.awbNumber === fetchForm.awbNumber);
    if (existingShipment) {
      // Set duplicate info for update mode
      setDuplicateAwbShipment(existingShipment);
      const ownerName = existingShipment.username || 'Unknown User';
      setDuplicateAwbMessage(
        `Entered AWB ${fetchForm.awbNumber} already assigned to ${ownerName}. Users switch to ${ownerName} for updates.`
      );
      return;
    }
    
    // Get selected customer details
    const selectedCustomerData = customers.find(c => c.id === parseInt(fetchForm.customer));
    
    // Map shipment type to service name
    const serviceNameMap = {
      'GMR Aero': 'gmr',
      'DCSC': 'dcsc'
    };
    const serviceName = serviceNameMap[fetchForm.selectBox2];
    
    setIsFetchingData(true);
    try {
      // Call scraper API to fetch shipment data
      const response = await apiCall(API_ENDPOINTS.SCRAPE_DATA, {
        method: 'POST',
        body: JSON.stringify({
          serviceName: serviceName,
          awbNumber: fetchForm.awbNumber
        })
      });

      const result = await response.json();
      
      console.log('Scraper API Response:', result);
      console.log('Fetched Data:', result.data);
      
      if (response.ok && result.success) {
        // Check if total_tables is 0 (invalid/wrong shipment type)
        if (result.data && result.data.total_tables === 0) {
          setFetchError('Invalid shipment type or AWB number. Please check your input and try again.');
          return;
        }
        
        // Clear any previous errors
        setFetchError('');
        
        // Parse and clean the scraped data
        const cleanedData = parseScrapedData(result.data);
        console.log('Cleaned Data:', cleanedData);
        
        // Fetch destination details
        let destinationData = {
          dest_code: '',
          dest_city: '',
          dest_country: ''
        };
        
        // Get destination from cleanedData - it's in the returned simplified object
        const destinationCity = cleanedData.destination || cleanedData.destinationCode || '';
        
        if (destinationCity) {
          try {
            console.log('Fetching destination for:', destinationCity, 'Service:', serviceName);
            
            // Build API URL based on service type
            const isGMR = serviceName.toLowerCase().includes('gmr');
            const apiUrl = isGMR 
              ? `${API_BASE_URL}/awb/destination?service=gmr&code=${destinationCity.toLowerCase()}`
              : `${API_BASE_URL}/awb/destination?service=${serviceName}&city=${destinationCity.toLowerCase()}`;
            
            const destResponse = await fetch(apiUrl);
            const destResult = await destResponse.json();
            
            console.log('Destination API Response:', destResult);
            
            if (destResponse.ok && destResult.data) {
              // Handle both array and object responses
              const destInfo = Array.isArray(destResult.data) ? destResult.data[0] : destResult.data;
              
              if (destInfo) {
                destinationData = {
                  dest_code: destInfo.code || destInfo.dest_code || destinationCity.toUpperCase(),
                  dest_city: destInfo.city || '',
                  dest_country: destInfo.country || ''
                };
                console.log('Destination Data:', destinationData);
              }
            } else {
              console.error('Destination API failed or no data:', destResult);
            }
          } catch (destError) {
            console.error('Error fetching destination:', destError);
          }
        } else {
          console.log('No destination city found in cleaned data');
        }
        
        // Store scraped data and open submit form
        setScrapedDataForSubmit({
          rawData: result.data,
          cleanedData: cleanedData,
          serviceName: serviceName,
          selectedCustomer: selectedCustomerData,
          destinationData: destinationData
        });
        
        // Open the submit form
        setShowSubmitForm(true);
        
        // Reset fetch form
        setFetchForm({
          customer: '',
          selectBox2: '',
          awbNumber: ''
        });
      } else {
        console.error('Failed to fetch data:', result);
        toast.error(`Failed to fetch data: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(`Error fetching data: ${error.message}`);
    } finally {
      setIsFetchingData(false);
    }
  };

  const handleFileUpload = (shipmentId, pdfNumber) => {
    // In a real app, this would upload the file
    const updatedShipments = shipments.map(s => {
      if (s.id === shipmentId) {
        return {
          ...s,
          [`pdf${pdfNumber}`]: `mock-pdf-url-${pdfNumber}.pdf`
        };
      }
      return s;
    });
    setShipments(updatedShipments);
    toast.success(`PDF ${pdfNumber} uploaded successfully`);
  };

  // Helper function to format number with Indian numbering
  const formatNumberInput = (value) => {
    if (!value) return '';
    const numStr = value.toString().replace(/,/g, '');
    const parts = numStr.split('.');
    let integerPart = parts[0];
    
    if (integerPart.length <= 3) {
      return parts.length > 1 ? integerPart + '.' + parts[1] : integerPart;
    }
    
    let result = '';
    let count = 0;
    
    for (let i = integerPart.length - 1; i >= 0; i--) {
      if (count === 3 || (count > 3 && (count - 3) % 2 === 0)) {
        result = ',' + result;
      }
      result = integerPart[i] + result;
      count++;
    }
    
    return parts.length > 1 ? result + '.' + parts[1] : result;
  };

  // Helper function to convert date to dd/mm/yyyy format
  const formatDateToDDMMYYYY = (dateStr) => {
    if (!dateStr) return dateStr;
    
    // Remove time portion (everything after space)
    const dateOnly = dateStr.split(' ')[0];
    
    // Parse date in format like "30-DEC-025" or "30-DEC-2025"
    const parts = dateOnly.split('-');
    if (parts.length !== 3) return dateOnly;
    
    const day = parts[0].padStart(2, '0');
    const monthStr = parts[1].toUpperCase();
    let year = parts[2];
    
    // Fix year format (025 -> 2025, 26 -> 2026)
    if (year.length === 3 && year.startsWith('0')) {
      year = '2' + year;
    } else if (year.length === 2) {
      year = '20' + year;
    }
    
    // Month mapping
    const months = {
      'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
      'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
      'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
    };
    
    const month = months[monthStr] || monthStr;
    
    return `${day}/${month}/${year}`;
  };

  // Helper function to process date arrays with deduplication
  const formatDateArray = (dates) => {
    if (!dates || !Array.isArray(dates)) return null;
    const filtered = dates.filter(Boolean);
    if (filtered.length === 0) return null;
    
    // Format dates and remove duplicates
    const formattedDates = filtered.map(date => formatDateToDDMMYYYY(date));
    const uniqueDates = [...new Set(formattedDates)];
    
    return uniqueDates.join(' & ');
  };

  // Validate AWB PDF by extracting AWB number from the uploaded PDF
  const validateAwbPdf = async (file) => {
    setIsValidatingAwbPdf(true);
    setAwbPdfError('');
    
    try {
      // Get and decode token properly
      const encodedToken = localStorage.getItem('token');
      let token = null;
      
      if (encodedToken) {
        try {
          token = atob(atob(encodedToken)); // Double decode
        } catch (error) {
          console.error('Token decode error:', error);
        }
      }
      
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const formData = new FormData();
      formData.append('pdf', file);
      
      // Call extract-awb API to get AWB number from PDF
      const response = await fetch(`${API_BASE_URL}/scraper/extract-awb`, {
        method: 'POST',
        headers: headers,
        body: formData
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to extract AWB number from PDF');
      }
      
      const extractedAwbNumber = (data.data?.awbNumber || '').toString().replace(/[^0-9]/g, '');
      const currentAwbNumber = (
        fetchForm.awbNumber || 
        scrapedDataForSubmit?.cleanedData?.awbNumber || 
        editingShipment?.awbNumber || 
        ''
      ).toString().replace(/[^0-9]/g, '');
      
      console.log('AWB PDF Validation - Extracted:', extractedAwbNumber, 'Expected:', currentAwbNumber);
      
      if (!extractedAwbNumber) {
        throw new Error('AWB number not found in the uploaded PDF. Please upload a valid AWB PDF.');
      }
      
      if (!currentAwbNumber) {
        throw new Error('AWB number missing. Please enter AWB number before uploading AWB PDF.');
      }
      
      if (extractedAwbNumber !== currentAwbNumber) {
        throw new Error(`AWB Mismatch - PDF contains AWB ${extractedAwbNumber} but expected ${currentAwbNumber}. Please upload the correct AWB PDF.`);
      }
      
      // AWB number matches — set the file
      setSubmitFormData(prev => ({
        ...prev,
        awb: file
      }));
      setAwbPdfError('');
      toast.success('AWB PDF verified successfully');
      
    } catch (error) {
      console.error('AWB PDF validation error:', error);
      setAwbPdfError(error.message || 'Error validating AWB PDF');
      toast.error(error.message || 'AWB PDF validation failed');
      // Don't set the file — reject it
      setSubmitFormData(prev => ({
        ...prev,
        awb: null
      }));
    } finally {
      setIsValidatingAwbPdf(false);
    }
  };

  const handleSubmitFormChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      if (name === 'leo') {
        const leoFiles = Array.from(files);
        setSubmitFormData(prev => ({
          ...prev,
          [name]: leoFiles
        }));
        // If LEO PDFs are uploaded, trigger API calls to fetch additional data (one by one)
        if (leoFiles.length > 0) {
          handleLEOPDFUpload(leoFiles);
        }
      } else if (name === 'inv') {
        // Restrict invPdf uploads to sbNo count
        const invFiles = Array.from(files);
        // Get SB numbers count from scrapedDataForSubmit if available
        const sbCount = (scrapedDataForSubmit?.cleanedData?.sbNumbers || []).length;
        if (sbCount && invFiles.length > sbCount) {
          toast.error(`You can upload maximum ${sbCount} Invoice PDF(s) as per SB numbers.`);
          return;
        }
        setSubmitFormData(prev => ({
          ...prev,
          [name]: invFiles
        }));
      } else if (name === 'awb') {
        // Accept AWB PDF directly without validation
        const awbFile = files[0];
        if (awbFile) {
          setSubmitFormData(prev => ({
            ...prev,
            awb: awbFile
          }));
          toast.success('AWB PDF uploaded successfully');
        }
      } else {
        setSubmitFormData(prev => ({
          ...prev,
          [name]: files[0]
        }));
      }
    } else {
      // Format numeric fields (DBK, Rodtep, Rosctl, IGST)
      if (['DBK', 'Rodtep', 'Rosctl', 'IGST'].includes(name)) {
        // Remove existing commas and keep only numbers and decimal point
        const cleanValue = value.replace(/,/g, '').replace(/[^\d.]/g, '');
        // Format with Indian numbering
        const formattedValue = formatNumberInput(cleanValue);
        setSubmitFormData(prev => ({
          ...prev,
          [name]: formattedValue
        }));
      } else if (name === 'customer_name') {
        // Handle customer name with autocomplete
        setSubmitFormData(prev => ({
          ...prev,
          [name]: value
        }));
        
        // Filter and show suggestions
        if (value.trim().length > 0) {
          const uniqueCustomerNames = [...new Set(shipments.map(s => s.customerName).filter(Boolean))];
          const filtered = uniqueCustomerNames.filter(name => 
            name.toLowerCase().includes(value.toLowerCase())
          );
          setCustomerSuggestions(filtered);
          setShowCustomerSuggestions(filtered.length > 0);
        } else {
          setShowCustomerSuggestions(false);
        }
      } else {
        setSubmitFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
  };

  const normalizeSbNumber = (value) => (value || '')
    .toString()
    .trim()
    .replace(/\s+/g, '')
    .replace(/[^0-9A-Za-z/\-]/g, '');

  const joinWithAmp = (values) => {
    if (!values || !Array.isArray(values)) return '';
    const filtered = values.filter(v => v !== undefined && v !== null && String(v).trim() !== '');
    return filtered.join(' & ');
  };

  const handleLEOPDFUpload = async (files) => {
    setIsLoadingLEOData(true);
    setLeoFetchError('');
    setFetchedLEOData(null);
    setFetchedLEORecords([]);

    const scrapedSbNumbers = (scrapedDataForSubmit?.cleanedData?.sbNumbers || [])
      .filter(Boolean)
      .map(normalizeSbNumber);
    
    if (!scrapedSbNumbers.length) {
      setLeoFetchError('SB numbers not found in AWB details. Please fetch AWB details first.');
      setIsLoadingLEOData(false);
      return;
    }

    const leoFiles = Array.isArray(files) ? files : [files].filter(Boolean);

    if (leoFiles.length !== scrapedSbNumbers.length) {
      setLeoFetchError(`Please upload ${scrapedSbNumbers.length} LEO PDF(s) to match SB numbers from AWB details.`);
      setIsLoadingLEOData(false);
      return;
    }
    
    try {
      // Get and decode token properly
      const encodedToken = localStorage.getItem('token');
      let token = null;
      
      if (encodedToken) {
        try {
          token = atob(atob(encodedToken)); // Double decode
        } catch (error) {
          console.error('Token decode error:', error);
        }
      }
      
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const extractedRecords = [];

      for (const file of leoFiles) {
        const formData = new FormData();
        formData.append('pdf', file);
        
        // Call API to extract LEO PDF data (one by one)
        const response = await fetch(`${API_BASE_URL}/scraper/air-export-extract`, {
          method: 'POST',
          headers: headers,
          body: formData
        });
        
        const data = await response.json();
        
        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Failed to extract LEO data');
        }

        const fetchedData = data.data || {};

        const modeValue = fetchedData.mode?.trim().toUpperCase() || 'AIR';
        
        // Validate AWB Number - must match the AWB entered in the form
        const normalizeAwbNumber = (value) => (value || '')
          .toString()
          .trim()
          .replace(/\s+/g, '')
          .replace(/[^0-9]/g, '');

        const pdfAwbRaw = fetchedData.awbNumber?.toString() || fetchedData.awb?.toString() || '';
        const enteredAwbRaw = fetchForm.awbNumber || scrapedDataForSubmit?.cleanedData?.awbNumber || editingShipment?.awbNumber || '';

        const pdfAwbNumber = normalizeAwbNumber(pdfAwbRaw);
        const enteredAwbNumber = normalizeAwbNumber(enteredAwbRaw);
        
        console.log('AWB Validation Check - PDF AWB:', pdfAwbNumber, 'Entered AWB:', enteredAwbNumber);
        
        if (!pdfAwbNumber) {
          throw new Error('LEO PDF not correct - AWB number not found in document');
        }
        
        if (!enteredAwbNumber) {
          throw new Error('AWB number missing in form. Please enter AWB number before uploading LEO PDF.');
        }

        if (pdfAwbNumber !== enteredAwbNumber) {
          throw new Error(`AWB Mismatch - PDF contains ${pdfAwbNumber} but you entered ${enteredAwbNumber}. Please upload correct LEO PDF.`);
        }

        const sbNumberRaw = fetchedData.sbNo || fetchedData.sbNumber || fetchedData.sb_no || fetchedData.shippingBillNo || fetchedData.shipping_bill_no || '';
        const sbNumber = normalizeSbNumber(sbNumberRaw);

        extractedRecords.push({
          sbNumber: sbNumber,
          awbNumber: pdfAwbNumber,
          IGST: fetchedData.igstAmt || fetchedData.igst_amt || '',
          DBK: fetchedData.dbkClaim || fetchedData.dbk_claim || '',
          Rodtep: fetchedData.rodtepAmt || fetchedData.rodtep_amt || '',
          Rosctl: fetchedData.rosctlAmt || fetchedData.rosctl_amt || '',
          customerName: fetchedData.customerName || fetchedData.customer_name || '',
          buyerName: fetchedData.buyerName || fetchedData.buyer_name || '',
          invoiceAmount: fetchedData.invoiceAmount || fetchedData.invoice_amount || '',
          currency: fetchedData.currency || '',
          exchangeRate: fetchedData.exchangeRate || fetchedData.exchange_rate || '',
          fileName: fetchedData.fileName || fetchedData.file_name || '',
          mode: modeValue,
          exporterName: fetchedData.exporterName || fetchedData.exporter_name || '',
          exporterAddress: fetchedData.exporterAddress || fetchedData.exporter_address || '',
          gstNo: (() => {
            const raw = fetchedData.gstNo || fetchedData.gst_no || fetchedData.gstin || '';
            // Extract 15-char GST number pattern from any prefixed string like "07HRHPK7019A1ZX GSN"
            const match = raw.match(/[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]/);
            return match ? match[0] : raw.replace(/^[^0-9]*/,'').trim();
          })(),
          invoiceNumber: (() => {
            const raw = (fetchedData.invoiceDetails || '').trim();
            const dateMatch = raw.match(/\d{2}\/\d{2}\/\d{4}\s*$/);
            return dateMatch ? raw.slice(0, dateMatch.index).trim() : raw;
          })(),
          invoiceDate: (() => {
            const raw = (fetchedData.invoiceDetails || '').trim();
            const dateMatch = raw.match(/(\d{2}\/\d{2}\/\d{4})\s*$/);
            return dateMatch ? dateMatch[1] : '';
          })(),
          // Destination fields from LEO PDF (fallback for blank destCode/destCity/destCountry)
          // API returns countryOfDischarge & portOfDischarge like "LGW (LONDON - GATWICK)"
          // Parse portOfDischarge to extract code and city name
          countryOfFinalDestination: fetchedData.countryOfFinalDestination || fetchedData.countryOfDischarge || fetchedData.country_of_final_destination || '',
          portOfDischargeCode: fetchedData.portOfDischargeCode || fetchedData.port_of_discharge_code || (() => {
            const raw = fetchedData.portOfDischarge || fetchedData.port_of_discharge || '';
            const match = raw.match(/^([A-Z0-9]+)\s*\(/);
            return match ? match[1] : '';
          })(),
          portOfDischarge: fetchedData.portOfDischarge || fetchedData.port_of_discharge || '',
          leoDate: fetchedData.leoDate || fetchedData.leo_date || ''
        });
      }

      const extractedSbNumbers = extractedRecords.map(r => normalizeSbNumber(r.sbNumber)).filter(Boolean);
      const scraperSbSet = new Set(scrapedSbNumbers);
      const extractedSbSet = new Set(extractedSbNumbers);

      const notInScraper = extractedSbNumbers.filter(sb => !scraperSbSet.has(sb));
      if (notInScraper.length > 0) {
        throw new Error(`SB number mismatch. LEO PDF has SB: ${notInScraper.join(', ')} not found in AWB details.`);
      }

      const missingFromExtraction = scrapedSbNumbers.filter(sb => !extractedSbSet.has(sb));
      if (missingFromExtraction.length > 0) {
        throw new Error(`LEO PDF missing for SB number(s): ${missingFromExtraction.join(', ')}`);
      }

      const recordsBySb = new Map();
      extractedRecords.forEach(r => recordsBySb.set(normalizeSbNumber(r.sbNumber), r));
      const orderedRecords = scrapedSbNumbers.map(sb => recordsBySb.get(sb));

      const aggregateField = (field) => orderedRecords.map(r => r?.[field] || '');
      const firstNonEmpty = (field) => orderedRecords.find(r => r?.[field])?.[field] || '';

      const dbkValues = aggregateField('DBK');
      const rodtepValues = aggregateField('Rodtep');
      const rosctlValues = aggregateField('Rosctl');
      const igstValues = aggregateField('IGST');
      const fcValues = aggregateField('invoiceAmount');

      const aggregatedData = {
        awbNumber: orderedRecords[0]?.awbNumber || '',
        IGST: joinWithAmp(igstValues),
        DBK: joinWithAmp(dbkValues),
        Rodtep: joinWithAmp(rodtepValues),
        Rosctl: joinWithAmp(rosctlValues),
        customerName: firstNonEmpty('customerName'),
        buyerName: firstNonEmpty('buyerName'),
        invoiceAmount: joinWithAmp(fcValues),
        currency: firstNonEmpty('currency'),
        exchangeRate: firstNonEmpty('exchangeRate'),
        fileName: joinWithAmp(aggregateField('fileName')),
        mode: 'AIR',
        sbNumbers: scrapedSbNumbers,
        DBKValues: dbkValues,
        RodtepValues: rodtepValues,
        RosctlValues: rosctlValues,
        IGSTValues: igstValues,
        fcValues: fcValues,
        exporterName: firstNonEmpty('exporterName'),
        exporterAddress: firstNonEmpty('exporterAddress'),
        gstNo: firstNonEmpty('gstNo'),
        invoiceNumber: aggregateField('invoiceNumber'),
        invoiceDate: aggregateField('invoiceDate'),
        leoDate: aggregateField('leoDate'),
        // Destination fields from LEO PDF
        countryOfFinalDestination: firstNonEmpty('countryOfFinalDestination'),
        portOfDischargeCode: firstNonEmpty('portOfDischargeCode'),
        portOfDischarge: firstNonEmpty('portOfDischarge')
      };

      const primaryCustomerName = orderedRecords.find(r => r?.customerName)?.customerName || '';

      setFetchedLEORecords(orderedRecords);
      setFetchedLEOData(aggregatedData);

      // Update scrapedDataForSubmit cleanedData with fresh LEO values so preview reflects new upload
      setScrapedDataForSubmit(prev => prev ? {
        ...prev,
        cleanedData: {
          ...prev.cleanedData,
          buyerName: aggregatedData.buyerName || prev.cleanedData?.buyerName,
          customerName: aggregatedData.customerName || prev.cleanedData?.customerName,
          IGST: aggregatedData.IGST || prev.cleanedData?.IGST,
          DBK: aggregatedData.DBK || prev.cleanedData?.DBK,
          Rodtep: aggregatedData.Rodtep || prev.cleanedData?.Rodtep,
          Rosctl: aggregatedData.Rosctl || prev.cleanedData?.Rosctl,
          invoiceAmount: aggregatedData.invoiceAmount || prev.cleanedData?.invoiceAmount,
          currency: aggregatedData.currency || prev.cleanedData?.currency,
          exporterName: aggregatedData.exporterName || prev.cleanedData?.exporterName,
          exporterAddress: aggregatedData.exporterAddress || prev.cleanedData?.exporterAddress,
          gstNo: aggregatedData.gstNo || prev.cleanedData?.gstNo,
          invoiceNumber: aggregatedData.invoiceNumber?.length ? aggregatedData.invoiceNumber : prev.cleanedData?.invoiceNumber,
          invoiceDate: aggregatedData.invoiceDate?.length ? aggregatedData.invoiceDate : prev.cleanedData?.invoiceDate,
          leoDate: aggregatedData.leoDate?.length ? aggregatedData.leoDate : prev.cleanedData?.leoDate,
        }
      } : prev);

      // Also update the form fields with fetched data
      setSubmitFormData(prev => ({
        ...prev,
        customer_name: primaryCustomerName || prev.customer_name,
        DBK: aggregatedData.DBK || prev.DBK,
        Rodtep: aggregatedData.Rodtep || prev.Rodtep,
        Rosctl: aggregatedData.Rosctl || prev.Rosctl,
        IGST: aggregatedData.IGST || prev.IGST
      }));
      
      console.log('LEO PDF data extracted (AIR mode confirmed):', extractedRecords);
    } catch (error) {
      console.error('Error uploading LEO PDF:', error);
      setLeoFetchError(error.message || 'Error processing LEO PDF');
    } finally {
      setIsLoadingLEOData(false);
    }
  };

  const handleSubmitFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!scrapedDataForSubmit) {
      toast.error('No data available');
      return;
    }

    if (!fetchedLEOData) {
      toast.error('LEO PDF data is required. Please upload and extract LEO PDF first.');
      return;
    }

    // Start loading animation
    setIsSubmitting(true);
    const startTime = Date.now();

    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId') || 1;
    
    const cleanedData = scrapedDataForSubmit.cleanedData;
    const serviceName = scrapedDataForSubmit.serviceName;
    const destinationData = scrapedDataForSubmit.destinationData || {};
    const selectedCustomer = scrapedDataForSubmit.selectedCustomer;
    
    // Determine shipment type
    const shipmentType = serviceName.toLowerCase().includes('dcsc') ? 'dcsc' : 'gmr';
    
    try {
      // Step 1: Upload PDFs to S3 if they exist
      let awbS3Key = null, leoS3Key = null, invS3Key = null;
      let leoS3Keys = [];
      let invS3Keys = [];
      
      const hasLeoFile = Array.isArray(submitFormData.leo)
        ? submitFormData.leo.length > 0
        : !!submitFormData.leo;

      if (submitFormData.awb || hasLeoFile || submitFormData.inv) {
        const filesToUpload = {
          awb: submitFormData.awb || null,
          leo: submitFormData.leo || null,
          inv: Array.isArray(submitFormData.inv) ? submitFormData.inv : submitFormData.inv ? [submitFormData.inv] : []
        };
        
        const awbNumber = cleanedData.awbNumber || editingShipment?.awbNumber;
        console.log('Uploading employee shipment PDFs for AWB:', awbNumber);

        // Belt-and-suspenders: uploadEmployeeShipmentPDFs already catches internally
        // and resolves to { success: false, error }, but a direct-to-S3 PUT can fail
        // at the network level (e.g. CORS) in ways that are worth guarding here too,
        // so a failed upload always surfaces as a toast rather than a crash.
        let uploadResults;
        try {
          uploadResults = await uploadEmployeeShipmentPDFs(filesToUpload, awbNumber);
        } catch (uploadError) {
          setIsSubmitting(false);
          toast.error(`Failed to upload files to S3: ${uploadError.message || 'network error'}`);
          return;
        }

        if (!uploadResults.success) {
          setIsSubmitting(false);
          toast.error('Failed to upload files to S3: ' + uploadResults.error);
          return;
        }
        
        // Extract s3Keys from results
        awbS3Key = uploadResults.keys.awbPdf || null;
        invS3Keys = Array.isArray(uploadResults.keys.invPdf) ? uploadResults.keys.invPdf : uploadResults.keys.invPdf ? [uploadResults.keys.invPdf] : [];
        const leoKeyValue = uploadResults.keys.leoPdfKey || uploadResults.keys.leoPdf || null;
        leoS3Keys = Array.isArray(leoKeyValue) ? leoKeyValue : (leoKeyValue ? [leoKeyValue] : []);
        leoS3Key = leoS3Keys.length === 1 ? leoS3Keys[0] : null;
        
        console.log('S3 Upload successful:', { awbS3Key, leoS3Key, invS3Key });
      }
      
      // Step 2: Prepare data for backend (with s3Keys instead of files)
      let requestBody;
      
      // If in edit mode, only send editable fields
      if (isEditMode && editingShipment) {
        // Get current user info
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const editedBy = currentUser.username || currentUser.name || 'Unknown';
        
        // Update mode - send editable fields + all scraped data
        const buyerNamesList = (fetchedLEORecords || []).map(r => r?.buyerName).filter(Boolean);
        const allSameAsConsignee = buyerNamesList.length > 0 && buyerNamesList.every(name =>
          (name || '').toString().trim().toUpperCase() === 'SAME AS CONSIGNEE'
        );
        const finalBuyerName = allSameAsConsignee
          ? (submitFormData.customer_name || fetchedLEOData?.customerName || '')
          : (fetchedLEOData?.buyerName || '');
        
        const dbkArray = fetchedLEOData?.DBKValues || (fetchedLEOData?.DBK ? [fetchedLEOData.DBK] : []);
        const rodtepArray = fetchedLEOData?.RodtepValues || (fetchedLEOData?.Rodtep ? [fetchedLEOData.Rodtep] : []);
        const rosctlArray = fetchedLEOData?.RosctlValues || (fetchedLEOData?.Rosctl ? [fetchedLEOData.Rosctl] : []);
        const igstArray = fetchedLEOData?.IGSTValues || (fetchedLEOData?.IGST ? [fetchedLEOData.IGST] : []);
        const fcArray = fetchedLEOData?.fcValues || (fetchedLEOData?.invoiceAmount ? [fetchedLEOData.invoiceAmount] : []);

        requestBody = {
          awbNumber: cleanedData.awbNumber || editingShipment.awbNumber,
          customerName: submitFormData.customer_name || fetchedLEOData?.customerName || '',
          DBK: dbkArray.map(v => (v || '').toString().replace(/,/g, '')),
          Rodtep: rodtepArray.map(v => (v || '').toString().replace(/,/g, '')),
          Rosctl: rosctlArray.map(v => (v || '').toString().replace(/,/g, '')),
          IGST: igstArray.map(v => (v || '').toString().replace(/,/g, '')),
          numberOfPackages: cleanedData.numberOfPackages || '',
          grossWeight: cleanedData.grossWeight || '',
          chargeableWeight: cleanedData.chargeableWeight || '',
          fcValue: fcArray.map(v => (v || '').toString().replace(/,/g, '')),
          fcType: fetchedLEOData?.currency || '',
          fcRate: fetchedLEOData?.exchangeRate || '',
          buyerName: finalBuyerName,
          gstNo: fetchedLEOData?.gstNo || '',
          invoiceNumber: Array.isArray(fetchedLEOData?.invoiceNumber) ? fetchedLEOData.invoiceNumber : (fetchedLEOData?.invoiceNumber ? [fetchedLEOData.invoiceNumber] : []),
          invoiceDate: Array.isArray(fetchedLEOData?.invoiceDate) ? fetchedLEOData.invoiceDate : (fetchedLEOData?.invoiceDate ? [fetchedLEOData.invoiceDate] : []),
          leoDate: Array.isArray(fetchedLEOData?.leoDate) ? fetchedLEOData.leoDate : (fetchedLEOData?.leoDate ? [fetchedLEOData.leoDate] : []),
          editedBy: editedBy
        };
        
        // Add S3 keys if files were uploaded
        if (awbS3Key) requestBody.awbPdf = awbS3Key;
        if (leoS3Keys.length) {
          requestBody.leoPdfKey = leoS3Keys;
        }
        if (invS3Keys.length) requestBody.invPdf = invS3Keys;
      } else {
        // Get current user info
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const editedBy = currentUser.username || currentUser.name || 'Unknown';
        
        // Create mode - send all fields
        const buyerNamesList = (fetchedLEORecords || []).map(r => r?.buyerName).filter(Boolean);
        const allSameAsConsignee = buyerNamesList.length > 0 && buyerNamesList.every(name =>
          (name || '').toString().trim().toUpperCase() === 'SAME AS CONSIGNEE'
        );
        const finalBuyerName = allSameAsConsignee
          ? (submitFormData.customer_name || fetchedLEOData?.customerName || '')
          : (fetchedLEOData?.buyerName || '');
        
        // Base request body common for both DCSC and GMR
        const dbkArray = fetchedLEOData?.DBKValues || (fetchedLEOData?.DBK ? [fetchedLEOData.DBK] : []);
        const rodtepArray = fetchedLEOData?.RodtepValues || (fetchedLEOData?.Rodtep ? [fetchedLEOData.Rodtep] : []);
        const rosctlArray = fetchedLEOData?.RosctlValues || (fetchedLEOData?.Rosctl ? [fetchedLEOData.Rosctl] : []);
        const igstArray = fetchedLEOData?.IGSTValues || (fetchedLEOData?.IGST ? [fetchedLEOData.IGST] : []);
        const fcArray = fetchedLEOData?.fcValues || (fetchedLEOData?.invoiceAmount ? [fetchedLEOData.invoiceAmount] : []);

        requestBody = {
          userID: selectedCustomer?.id || '',
          customerName: submitFormData.customer_name || fetchedLEOData?.customerName || '',
          shipmentType: shipmentType,
          DBK: dbkArray.map(v => (v || '').toString().replace(/,/g, '')),
          Rodtep: rodtepArray.map(v => (v || '').toString().replace(/,/g, '')),
          Rosctl: rosctlArray.map(v => (v || '').toString().replace(/,/g, '')),
          IGST: igstArray.map(v => (v || '').toString().replace(/,/g, '')),
          awbNumber: cleanedData.awbNumber || '',
          destCode: destinationData.dest_code || fetchedLEOData?.portOfDischargeCode || '',
          destCity: destinationData.dest_city || (() => {
            const raw = fetchedLEOData?.portOfDischarge || '';
            const match = raw.match(/\(([^)]+)\)/);
            return match ? match[1].trim() : raw;
          })(),
          destCountry: destinationData.dest_country || fetchedLEOData?.countryOfFinalDestination || '',
          fcValue: fcArray.map(v => (v || '').toString().replace(/,/g, '')),
          fcType: fetchedLEOData?.currency || '',
          fcRate: fetchedLEOData?.exchangeRate || '',
          buyerName: finalBuyerName,
          numberOfPackages: cleanedData.numberOfPackages || '',
          grossWeight: cleanedData.grossWeight || '',
          chargeableWeight: cleanedData.chargeableWeight || '',
          gstNo: fetchedLEOData?.gstNo || '',
          invoiceNumber: Array.isArray(fetchedLEOData?.invoiceNumber) ? fetchedLEOData.invoiceNumber : (fetchedLEOData?.invoiceNumber ? [fetchedLEOData.invoiceNumber] : []),
          invoiceDate: Array.isArray(fetchedLEOData?.invoiceDate) ? fetchedLEOData.invoiceDate : (fetchedLEOData?.invoiceDate ? [fetchedLEOData.invoiceDate] : []),
          leoDate: Array.isArray(fetchedLEOData?.leoDate) ? fetchedLEOData.leoDate : (fetchedLEOData?.leoDate ? [fetchedLEOData.leoDate] : [])
        };
        
        // Add S3 keys
        if (awbS3Key) requestBody.awbPdf = awbS3Key;
        if (leoS3Keys.length) {
          requestBody.leoPdfKey = leoS3Keys;
        }
        requestBody.invPdf = Array.isArray(invS3Key) ? invS3Key : invS3Key ? [invS3Key] : [];

        const fullDetailsSections = {
          awbDetails: cleanedData.awbDetails || {},
          statusDetails: cleanedData.statusDetails || [],
          paymentDetails: cleanedData.paymentDetails || [],
          shippingBillDetails: cleanedData.shippingBillDetails || [],
          loadingDetails: cleanedData.loadingDetails || [],
          locationDetails: cleanedData.locationDetails || [],
          screeningDetails: cleanedData.screeningDetails || []
        };
        
        // Add shipment type specific fields
        if (shipmentType === 'dcsc') {
          // DCSC specific structure
          requestBody.sbNumbers = cleanedData.sbNumbers || [];
          requestBody.sbDates = cleanedData.sbDates || [];
          requestBody.sbFobValues = cleanedData.sbFobValues || [];
          requestBody.flightNumbers = cleanedData.flightNumbers || [];
          requestBody.flightDates = cleanedData.flightDates || [];
          requestBody.paymentActivity = cleanedData.paymentActivities || [];
          requestBody.paymentDates = cleanedData.paymentDates || [];
          requestBody.paymentsAmount = cleanedData.paymentAmounts || [];
          
          // Send complete scraped data in fullDetails
          requestBody.fullDetails = fullDetailsSections;
          
        } else if (shipmentType === 'gmr') {
          // GMR specific fields
          requestBody.leoStatus = cleanedData.leoStatus || '';
          requestBody.tdgDate = cleanedData.tdgDate || '';
          requestBody.bagDate = cleanedData.bagDate || '';
          requestBody.sbNumbers = cleanedData.sbNumbers || [];
          requestBody.sbDates = cleanedData.sbDates || [];
          requestBody.sbPackages = cleanedData.sbPackages || [];
          requestBody.sbGrossWeights = cleanedData.sbGrossWeights || [];
          requestBody.sbFobValues = cleanedData.sbFobValues || [];
          requestBody.flightNumbers = cleanedData.flightNumbers || [];
          requestBody.flightDates = cleanedData.flightDates || [];
          requestBody.uldNumbers = cleanedData.uldNumbers || [];
          requestBody.uldTypes = cleanedData.uldTypes || [];
          requestBody.locations = cleanedData.locationDetails?.map(l => l.location) || [];
          requestBody.locationPieces = cleanedData.locationDetails?.map(l => l.pieces) || [];
          requestBody.screeningInfo = cleanedData.screeningDetails || [];
          requestBody.paymentActivity = cleanedData.paymentActivities || [];
          requestBody.paymentDates = cleanedData.paymentDates || [];
          requestBody.paymentsAmount = cleanedData.paymentAmounts || [];
          
          // Send complete scraped data in fullDetails
          requestBody.fullDetails = {
            awbNumber: cleanedData.awbNumber || '',
            serviceName: serviceName || cleanedData.awbDetails?.serviceName || '',
            serviceCode: cleanedData.serviceCode || '',
            extractionTime: cleanedData.extractionTime || cleanedData.awbDetails?.extractionTime || '',
            totalTables: cleanedData.totalTables || 0,
            ...fullDetailsSections
          };
        }
      }

      console.log('Submitting JSON', isEditMode ? 'for UPDATE' : 'for CREATE');
      
      // Get and decode token properly
      const encodedToken = localStorage.getItem('token');
      let token = null;
      
      if (encodedToken) {
        try {
          token = atob(atob(encodedToken)); // Double decode
        } catch (error) {
          console.error('Token decode error:', error);
          toast.error('Authentication token error. Please login again.');
          return;
        }
      }
      
      // Determine endpoint and method based on mode
      let apiEndpoint, method;
      
      if (isEditMode && editingShipment) {
        // Edit mode - use update endpoint
        apiEndpoint = `${API_BASE_URL}/awb/update?service=${editingShipment.shipmentType}&awb=${editingShipment.awbNumber}`;
        method = 'PUT';
      } else {
        // Create mode - use submit endpoint
        apiEndpoint = shipmentType === 'dcsc' 
          ? `${API_BASE_URL}/awb/dcsc`
          : `${API_BASE_URL}/awb/gmr`;
        method = 'POST';
      }
        
      const response = await fetch(apiEndpoint, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (response.ok) {
        // Calculate remaining time to ensure minimum 2 second loading
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, 2000 - elapsedTime);
        
        // Wait for remaining time if needed
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }
        
        // Stop loading animation
        setIsSubmitting(false);
        
        toast.success(isEditMode ? 'Shipment updated successfully!' : 'Shipment created successfully!');

        if (isEditMode) {
          const customerId = scrapedDataForSubmit?.selectedCustomer?.id || selectedCustomer?.id || editingShipment?.customerId || editingShipment?.userId;
          const customerUser = allUsers?.find(u => String(u.id) === String(customerId));
          sendShipmentUpdateEmail({
            to_email: customerUser?.email || '',
            name: customerUser?.name || customerUser?.username || submitFormData?.customer_name || 'Customer',
            order_id: editingShipment?.awbNumber || '',
            shipment_status: 'Updated',
            tracking_id: editingShipment?.awbNumber || '',
          }).catch(console.error);
        }

        // Call Invoice customer API with exporter details from LEO PDF
        try {
          const userId =
            scrapedDataForSubmit?.selectedCustomer?.id ||
            selectedCustomer?.id ||
            editingShipment?.customerId ||
            editingShipment?.userId;
          const numericUserId = Number(userId);
          const exporterName = fetchedLEOData?.exporterName || submitFormData.customer_name || '';
          const exporterAddress = fetchedLEOData?.exporterAddress || '';
          const gstNo = fetchedLEOData?.gstNo || '';
          // Only call if we have all required fields (gstin must be 15 chars)
          if (exporterName && Number.isFinite(numericUserId) && numericUserId > 0 && gstNo && gstNo.length === 15) {
            apiCall(API_ENDPOINTS.INVOICE_ADD_CUSTOMER, {
              method: 'POST',
              body: JSON.stringify({
                userId: numericUserId,
                name: exporterName,
                address: exporterAddress,
                gstin: gstNo
              })
            }).catch(err => console.error('Invoice customer API error:', err));
          }
        } catch (invoiceErr) {
          console.error('Invoice customer call failed:', invoiceErr);
        }

        // Close form and reset
        setShowSubmitForm(false);
        setScrapedDataForSubmit(null);
        setEditingShipment(null);
        setIsEditMode(false);
        setFetchedLEOData(null);
        setFetchedLEORecords([]);
        setLeoFetchError('');
        setAwbPdfError('');
        setSubmitFormData({
          customer_name: '',
          DBK: '0',
          Rodtep: '0',
          Rosctl: '0',
          IGST: '0',
          awb: null,
          leo: [],
          inv: null
        });
        
        // Small delay to ensure backend has processed the creation
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Refetch all shipments to update table
        await refetchShipments();
        
        // Trigger notification for other dashboards
        window.dispatchEvent(new CustomEvent('shipmentCreated', {
          detail: { awbNumber: requestBody.awbNumber, timestamp: new Date().toISOString() }
        }));
        
        // Also use localStorage for cross-tab communication
        localStorage.setItem('newShipmentNotification', JSON.stringify({
          awbNumber: requestBody.awbNumber,
          timestamp: new Date().toISOString()
        }));
      } else {
        setIsSubmitting(false);
        toast.error(`Failed to submit: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error('Error submitting form:', error);
      toast.error(`Error: ${error.message}`);
    }
  };

  const handleCancelSubmitForm = () => {
    setShowSubmitForm(false);
    setScrapedDataForSubmit(null);
    setEditingShipment(null);
    setIsEditMode(false);
    setShowCustomerSuggestions(false);
    setFetchedLEOData(null);
    setFetchedLEORecords([]);
    setLeoFetchError('');
    setAwbPdfError('');
    setSubmitFormData({
      customer_name: '',
      DBK: '0',
      Rodtep: '0',
      Rosctl: '0',
      IGST: '0',
      awb: null,
      leo: [],
      inv: null
    });
  };

  const handleEditShipment = (shipment) => {
    setEditingShipment(shipment);
    setIsEditMode(true);

    const toArray = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value.filter(Boolean);
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) return parsed.filter(Boolean);
        } catch {
          if (value.includes(' & ')) return value.split(' & ').map(v => v.trim()).filter(Boolean);
        }
        return [value].filter(Boolean);
      }
      return [value].filter(Boolean);
    };
    
    // Populate submit form with shipment data (format numbers)
    setSubmitFormData({
      customer_name: shipment.customerName || '',
      DBK: formatNumberInput(shipment.dbk || '0'),
      Rodtep: formatNumberInput(shipment.rodtep || '0'),
      Rosctl: formatNumberInput(shipment.rosctl || '0'),
      IGST: formatNumberInput(shipment.igst || '0'),
      awb: null,
      leo: [],
      inv: null
    });
    
    // Create scrapedDataForSubmit with ALL shipment's existing data
    const sbNumbers = toArray(shipment.sbNumbers || shipment.sbNumber);
    const sbDates = toArray(shipment.sbDates || shipment.sbDate);
    const sbFobValues = toArray(shipment.sbFobValues || shipment.fob);
    const flightNumbers = toArray(shipment.flightNumbers || shipment.flightNo);
    const flightDates = toArray(shipment.flightDates || shipment.flightDate);
    const paymentAmounts = toArray(shipment.paymentsAmount || shipment.paymentAmounts || shipment.amount);
    const paymentActivities = toArray(shipment.paymentActivity || shipment.paymentActivities || shipment.activity);

    setScrapedDataForSubmit({
      rawData: {},
      cleanedData: {
        awbNumber: shipment.awbNumber,
        portCode: shipment.portCode || '',
        destination: shipment.cityCode || '',
        currentStatus: shipment.status || '',
        grossWeight: shipment.grossWeight || shipment.weight || '',
        sbNumber: shipment.sbNumber || sbNumbers.join(' & ') || '',
        sbNumbers,
        sbDate: shipment.sbDate || '',
        sbDates,
        sbFobValues,
        flightNo: shipment.flightNo || flightNumbers.join(' & ') || '',
        flightNumbers,
        flightDate: shipment.flightDate || '',
        flightDates,
        numberOfPackages: shipment.numberOfPackages || shipment.totalPkt || shipment.totalPkts || '',
        chargeableWeight: shipment.chargeableWeight || shipment.chWeight || '',
        paymentAmounts,
        paymentActivities,
        fcValue: shipment.fcValue || '',
        fcType: shipment.fcType || '',
        fcRate: shipment.fcRate || '',
        buyerName: shipment.buyerName || '',
              consigneeName: shipment.consigneeName || shipment.consignee_name || shipment.customerName || '',
        destCode: shipment.cityCode || shipment.destCode || '',
        destCity: shipment.destCity || '',
        destCountry: shipment.destCountry || ''
      },
      serviceName: shipment.shipmentType || 'gmr',
      selectedCustomer: {
        id: shipment.customerId,
        name: shipment.username
      },
      destinationData: {
        dest_code: shipment.cityCode || shipment.destCode || '',
        dest_city: shipment.destCity || '',
        dest_country: shipment.destCountry || ''
      }
    });

    const leoDbkValues = toArray(shipment.dbk || shipment.DBK || shipment.dbkClaim);
    const leoRodtepValues = toArray(shipment.rodtep || shipment.Rodtep || shipment.rodtepAmt);
    const leoRosctlValues = toArray(shipment.rosctl || shipment.Rosctl || shipment.rosctlAmt);
    const leoIgstValues = toArray(shipment.igst || shipment.IGST || shipment.igstAmt);
    const leoFcValues = toArray(shipment.fcValue || shipment.invoiceAmount);

    setFetchedLEOData({
      awbNumber: shipment.awbNumber || '',
      IGST: leoIgstValues.join(' & '),
      DBK: leoDbkValues.join(' & '),
      Rodtep: leoRodtepValues.join(' & '),
      Rosctl: leoRosctlValues.join(' & '),
      customerName: shipment.customerName || shipment.username || '',
      consigneeName: shipment.consigneeName || shipment.consignee_name || '',
      buyerName: shipment.buyerName || '',
      invoiceAmount: leoFcValues.join(' & '),
      currency: shipment.fcType || '',
      exchangeRate: shipment.fcRate || '',
      fileName: '',
      mode: 'AIR',
      sbNumbers,
      DBKValues: leoDbkValues,
      RodtepValues: leoRodtepValues,
      RosctlValues: leoRosctlValues,
      IGSTValues: leoIgstValues,
      fcValues: leoFcValues,
      gstNo: shipment.gstNo || shipment.gst_no || '',
      exporterName: shipment.exporterName || shipment.exporter_name || '',
      exporterAddress: shipment.exporterAddress || shipment.exporter_address || '',
      invoiceNumber: shipment.invoiceNumber || shipment.invoice_number || '',
      invoiceDate: shipment.invoiceDate || shipment.invoice_date || ''
    });
    setFetchedLEORecords([]);
    setLeoFetchError('');
    
    setShowSubmitForm(true);
  };



  const handleDeleteShipment = (shipment) => {
    setShipmentToDelete(shipment);
    setShowDeleteModal(true);
  };

  const confirmDeleteShipment = async () => {
    if (!shipmentToDelete) return;
    
    try {
      console.log('Deleting shipment:', shipmentToDelete.shipmentType, shipmentToDelete.awbNumber);
      
      const response = await apiCall(`/awb/delete?service=${shipmentToDelete.shipmentType}&awb=${shipmentToDelete.awbNumber}`, {
        method: 'DELETE'
      });

      const result = await response.json();
      console.log('Delete response:', result);

      if (response.ok && result.success) {
        setShowDeleteModal(false);
        setShipmentToDelete(null);
        
        // Small delay to ensure backend has processed the delete
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Refetch shipments
        await refetchShipments();
      } else {
        toast.error(result.message || 'Failed to delete shipment');
      }
    } catch (error) {
      console.error('Error deleting shipment:', error);
      toast.error('An error occurred while deleting the shipment');
    }
  };

  const cancelDeleteShipment = () => {
    setShowDeleteModal(false);
    setShipmentToDelete(null);
  };

  const handlePdfDeleteClick = (pdfType, mode) => {
    setPdfToDelete({ pdfType, mode });
    setShowPdfDeleteModal(true);
  };

  const confirmPdfDelete = () => {
    if (!pdfToDelete) return;
    
    const { pdfType, mode } = pdfToDelete;
    
    if (mode === 'editing') {
      // Map pdfType to the correct field name in editingShipment
      const fieldName = pdfType === 'awb' ? 'awbPdf' : pdfType;
      setEditingShipment(prev => ({
        ...prev,
        [fieldName]: null
      }));
    } else if (mode === 'submit') {
      setSubmitFormData(prev => ({
        ...prev,
        [pdfType]: pdfType === 'leo' ? [] : null
      }));
      if (pdfType === 'leo') {
        setFetchedLEOData(null);
        setFetchedLEORecords([]);
        setLeoFetchError('');
      }
      if (pdfType === 'awb') {
        setAwbPdfError('');
      }
    }
    
    toast.success(`${pdfType.toUpperCase()} PDF removed`);
    setShowPdfDeleteModal(false);
    setPdfToDelete(null);
  };

  const cancelPdfDelete = () => {
    setShowPdfDeleteModal(false);
    setPdfToDelete(null);
  };

  const handleViewPDF = (pdfUrl, pdfName) => {
    if (pdfUrl) {
      const pdfWindow = window.open('', '_blank');
      pdfWindow.document.write(`
        <html>
          <head>
            <title>${pdfName}</title>
            <style>
              body { margin: 0; padding: 0; }
              iframe { width: 100vw; height: 100vh; border: none; }
            </style>
          </head>
          <body>
            <iframe src="${pdfUrl}"></iframe>
          </body>
        </html>
      `);
    }
  };

  const normalizePdfList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.filter(Boolean);
      } catch {
        if (value.includes(',')) {
          return value.split(',').map(v => v.trim()).filter(Boolean);
        }
        return [value];
      }
      return [value];
    }
    return [value];
  };

  const openMultipleTabs = (urls, label = 'documents') => {
    if (!urls || urls.length === 0) return;
    let blockedCount = 0;

    urls.forEach((url) => {
      const win = window.open(url, '_blank');
      if (!win) {
        blockedCount += 1;
      } else {
        try {
          win.opener = null;
        } catch (e) {
          // no-op
        }
      }
    });

    if (blockedCount > 0 && urls.length > 1) {
      toast.error(`Popup blocked. Please open the remaining ${label} from the list.`);

      const helper = window.open('', '_blank');
      if (helper) {
        const listItems = urls.map((url, index) => `<li style="margin: 8px 0;"><a href="${url}" target="_blank" rel="noopener noreferrer">${label} ${index + 1}</a></li>`).join('');
        helper.document.write(`
          <html>
            <head>
              <title>Open ${label}</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 16px; }
                h2 { margin: 0 0 12px; }
              </style>
            </head>
            <body>
              <h2>Open ${label}</h2>
              <p>Click each link to open in a new tab.</p>
              <ul>${listItems}</ul>
            </body>
          </html>
        `);
      }
    }
  };

  // Sea Export Handlers
  const handleSeaExportSubmit = async (formData) => {
    try {
      console.log('📦 Starting Sea Export submission...');

      const normalizedBlNumber = (formData.blNumber || '').toString().trim();
      const normalizedContainerNos = Array.isArray(formData.containerNo)
        ? formData.containerNo.map(v => String(v || '').trim().toUpperCase())
        : String(formData.containerNo || '')
        .split(/\s*&\s*|\s*,\s*/)
        .map(v => v.trim().toUpperCase())
        .filter(Boolean);
      const normalizedGstNo = (formData.gstNo || '').toString().trim().toUpperCase();
      const normalizedQty = Math.max(1, parseInt(formData.containerQty, 10) || 1);
      const primaryBlNumber = normalizedBlNumber || 'unknown';

      if (!normalizedBlNumber) {
        toast.error('Please enter B/L Number.');
        return;
      }

      if (normalizedBlNumber.length < 8) {
        toast.error('B/L Number must be at least 8 characters.');
        return;
      }

      const containerPattern = /^[A-Z]{4}\d{7}$/;
      const invalidContainerIndex = normalizedContainerNos.findIndex(cn => cn && (!containerPattern.test(cn) || !['U', 'J', 'Z'].includes(cn[3])));

      if (invalidContainerIndex !== -1) {
        toast.error(`Invalid Container No ${invalidContainerIndex + 1}. Use: ABCU1234567`);
        return;
      }

      if (normalizedContainerNos.length > 0 && normalizedContainerNos.length !== normalizedQty) {
        toast.error(`Container Qty is ${normalizedQty}. Please provide ${normalizedQty} container numbers.`);
        return;
      }
      
      // Step 1: Upload PDFs to S3
      const normalizePdfList = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value.filter(Boolean);
        return [value];
      };

      const getSbCount = () => {
        const sbRaw = formData.sbNo || '';
        const list = String(sbRaw)
          .split(/\s*&\s*|\s*,\s*/)
          .map(v => v.trim())
          .filter(Boolean);
        return Math.max(1, list.length);
      };

      const leoCandidates = normalizePdfList(formData.leoPdf);
      const sbCount = getSbCount();
      if (sbCount > 1 && leoCandidates.length > 0 && leoCandidates.length !== sbCount) {
        toast.error(`Please upload exactly ${sbCount} LEO PDFs as per SB count.`);
        return;
      }

      const files = {
        blPdf: formData.blPdf,
        leoPdf: leoCandidates.length === 1 ? leoCandidates[0] : null,
        invPlPdf: formData.invPlPdf
      };
      
      const uploadResult = await uploadSeaExportShipmentPDFs(files, primaryBlNumber);
      
      if (!uploadResult.success) {
        toast.error(`Upload failed: ${uploadResult.error}`);
        return;
      }
      
      console.log('✅ PDFs uploaded to S3:', uploadResult.keys);

      let leoPdfKey = uploadResult.keys.leoPdf || '';
      if (leoCandidates.length > 1) {
        const uploadedLeoKeys = [];
        for (let i = 0; i < leoCandidates.length; i += 1) {
          const singleLeoUpload = await uploadSeaExportShipmentPDFs({ leoPdf: leoCandidates[i] }, `${primaryBlNumber}_${i + 1}`);
          if (!singleLeoUpload.success) {
            toast.error(`LEO PDF ${i + 1} upload failed: ${singleLeoUpload.error}`);
            return;
          }
          if (singleLeoUpload.keys.leoPdf) uploadedLeoKeys.push(singleLeoUpload.keys.leoPdf);
        }
        leoPdfKey = uploadedLeoKeys.join(' & ');
      }
      
      // Get current user info for editedBy
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const editedBy = currentUser.username || currentUser.name || 'Unknown';
      
      // Step 2: Prepare data for backend API
      const apiData = {
        portCode: formData.portCode || '',
        sbNo: formData.sbNo || '',
        sbDate: formData.sbDate || '',
        pkg: formData.pkg || '',
        gwt: formData.gwt || '',
        portOfLoading: formData.portOfLoading || '',
        stateOfOrigin: formData.stateOfOrigin || '',
        portOfDischargeCode: formData.portOfDischargeCode || '',
        portOfDischarge: formData.portOfDischarge || '',
        portOfFinalDestinationCode: formData.portOfFinalDestinationCode || '',
        portOfFinalDestination: formData.portOfFinalDestination || '',
        countryOfFinalDestination: formData.countryOfFinalDestination || '',
        exporterName: formData.exporterName || '',
        exporterAddress: formData.exporterAddress || '',
        gstNo: normalizedGstNo,
        buyerName: formData.buyerName || '',
        buyerAddress: formData.buyerAddress || '',
        consigneeName: formData.consigneeName || '',
        mode: formData.mode || '',
        lut: formData.lut || '',
        fobValue: formData.fobValue || '',
        dbkClaim: formData.dbkClaim || '',
        igstAmt: formData.igstAmt || '',
        cessAmt: formData.cessAmt || '',
        igstValue: formData.igstValue || '',
        rodtepAmt: formData.rodtepAmt || '',
        rosctlAmt: formData.rosctlAmt || '',
        invNo: formData.invNo || '',
        invAmt: formData.invAmt || '',
        currency: formData.currency || '',
        leoDate: formData.leoDate || '',
        invoiceNoDt: formData.invoiceNoDt || '',
        blNumber: normalizedBlNumber,
        containerNo: normalizedContainerNos,
        containerQty: String(normalizedQty),
        leoPdf: leoPdfKey,
        blPdf: uploadResult.keys.blPdf || '',
        invPlPdf: uploadResult.keys.invPlPdf || '',
        userId: formData.customerId || null,
        editedBy: editedBy
      };
      
      console.log('📤 Sending data to backend API:');
      console.log(JSON.stringify(apiData, null, 2));
      
      // Step 3: Submit to backend API
      const response = await fetch(`${API_BASE_URL}/sea-export`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(apiData)
      });
      
      const result = await response.json();
      console.log('📥 Backend API response:', JSON.stringify(result, null, 2));
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to submit sea export data');
      }

      // Save customer GST details using same API as Air flow
      try {
        const exporterName = (formData.exporterName || '').toString().trim();
        const exporterAddress = (formData.exporterAddress || '').toString().trim();
        const numericUserId = Number(formData.customerId);

        if (exporterName && Number.isFinite(numericUserId) && numericUserId > 0 && normalizedGstNo && normalizedGstNo.length === 15) {
          apiCall(API_ENDPOINTS.INVOICE_ADD_CUSTOMER, {
            method: 'POST',
            body: JSON.stringify({
              userId: numericUserId,
              name: exporterName,
              address: exporterAddress,
              gstin: normalizedGstNo
            })
          }).catch(err => console.error('Invoice customer API error (Sea):', err));
        }
      } catch (invoiceErr) {
        console.error('Invoice customer call failed (Sea):', invoiceErr);
      }
      
      // Step 4: Refresh sea export list from backend
      toast.success('Sea Export shipment created successfully!');
      
      // Fetch updated list
      const listResponse = await apiCall(API_ENDPOINTS.GET_SEA_EXPORT, {
        method: 'GET'
      });
      
      const listData = await listResponse.json();
      
      if (listResponse.ok && listData.success) {
        const formattedShipments = (listData.data || []).map(s => ({
          id: s.id,
          portCode: s.port_code || s.portCode || '-',
          sbNo: s.sb_no || s.sbNo || '-',
          sbDate: s.sb_date || s.sbDate || '-',
          pkg: s.pkg || '-',
          gwt: s.gwt || '-',
          portOfLoading: s.port_of_loading || s.portOfLoading || '-',
          stateOfOrigin: s.state_of_origin || s.stateOfOrigin || '-',
          portOfDischargeCode: s.port_of_discharge_code || s.portOfDischargeCode || '-',
          portOfDischarge: s.port_of_discharge || s.portOfDischarge || '-',
          portOfFinalDestinationCode: s.port_of_final_destination_code || s.portOfFinalDestinationCode || '-',
          portOfFinalDestination: s.port_of_final_destination || s.portOfFinalDestination || '-',
          countryOfFinalDestination: s.country_of_final_destination || s.countryOfFinalDestination || '-',
          exporterName: s.exporter_name || s.exporterName || '-',
          exporterAddress: s.exporter_address || s.exporterAddress || '-',
          gstNo: s.gst_no || s.gstNo || '-',
          buyerName: s.buyer_name || s.buyerName || '-',
          consigneeName: s.consignee_name || s.consigneeName || '-',
          mode: s.mode || '-',
          lut: s.lut || '-',
          fobValue: s.fob_value || s.fobValue || '-',
          dbkClaim: s.dbk_claim || s.dbkClaim || '-',
          igstAmt: s.igst_amt || s.igstAmt || '-',
          cessAmt: s.cess_amt || s.cessAmt || '-',
          igstValue: s.igst_value || s.igstValue || '-',
          rodtepAmt: s.rodtep_amt || s.rodtepAmt || '-',
          rosctlAmt: s.rosctl_amt || s.rosctlAmt || '-',
          invNo: s.inv_no || s.invNo || '-',
          invAmt: s.inv_amt || s.invAmt || '-',
          currency: s.currency || '-',
          leoDate: s.leo_date || s.leoDate || '-',
          invoiceNoDt: s.invoice_no_dt || s.invoiceNoDt || '-',
          blNumber: s.bl_number || s.blNumber || '-',
          containerNo: normalizeSeaContainerNoDisplay(s.container_no || s.containerNo),
          containerQty: s.container_qty || s.containerQty || '-',
          leoPdf: s.leo_pdf || s.leoPdf || '',
          blPdf: s.bl_pdf || s.blPdf || '',
          invPlPdf: s.inv_pl_pdf || s.invPlPdf || '',
          invoicePdf: s.invoice_pdf || s.invoicePdf || '',
          purPdf: s.pur_pdf || s.purPdf || '',
          customerId: s.user_id || s.userId || null,
          editedBy: s.edited_by || s.editedBy || '-',
          createdAt: s.created_at || s.createdAt || new Date().toISOString(),
          type: 'Sea-Export'
        }));
        
        setSeaExportBookings(formattedShipments);
        
        // Trigger notification for other dashboards
        window.dispatchEvent(new CustomEvent('shipmentCreated', {
          detail: { 
            blNumber: primaryBlNumber,
            type: 'Sea-Export',
            timestamp: new Date().toISOString() 
          }
        }));
        
        // Also use localStorage for cross-tab communication
        localStorage.setItem('newShipmentNotification', JSON.stringify({
          blNumber: primaryBlNumber,
          type: 'Sea-Export',
          timestamp: new Date().toISOString()
        }));
      }
    } catch (error) {
      console.error('❌ Sea Export submission error:', error);
      toast.error(`Failed to create sea export shipment: ${error.message}`);
    }
  };

  const handleCloseSeaExportForm = () => {
    // Form closes itself via modal
  };

  const handleEditSeaExport = async (updatedBooking) => {
    try {
      console.log('Editing sea export shipment:', updatedBooking);
      
      // Step 1: Check if new PDFs need to be uploaded
      let blPdfKey = updatedBooking.blPdf;
      let leoPdfKey = updatedBooking.leoPdf;
      let invPlPdfKey = updatedBooking.invPlPdf;

      const normalizePdfList = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value.filter(Boolean);
        return String(value)
          .split(/\s*&\s*|\s*,\s*/)
          .map(v => v.trim())
          .filter(Boolean);
      };
      
      // Check if any new PDFs were uploaded (File objects)
      const hasNewPdfs = 
        (updatedBooking.blPdf instanceof File) ||
        (updatedBooking.leoPdf instanceof File) ||
        (Array.isArray(updatedBooking.leoPdf) && updatedBooking.leoPdf.some(f => f instanceof File)) ||
        (updatedBooking.invPlPdf instanceof File);
      
      if (hasNewPdfs) {
        console.log('New PDFs detected, uploading to S3...');
        
        // Prepare files for upload (only new File objects)
        const leoCandidates = Array.isArray(updatedBooking.leoPdf)
          ? updatedBooking.leoPdf.filter(f => f instanceof File)
          : (updatedBooking.leoPdf instanceof File ? [updatedBooking.leoPdf] : []);

        const files = {
          blPdf: updatedBooking.blPdf instanceof File ? updatedBooking.blPdf : null,
          leoPdf: null,
          invPlPdf: updatedBooking.invPlPdf instanceof File ? updatedBooking.invPlPdf : null
        };
        
        // Use blNumber for S3 naming
        const blNumber = updatedBooking.blNumber || 'unknown';
        
        const uploadResult = await uploadSeaExportShipmentPDFs(files, blNumber);
        
        if (!uploadResult.success) {
          toast.error(`PDF upload failed: ${uploadResult.error}`);
          return;
        }
        
        console.log('✅ New PDFs uploaded:', uploadResult.keys);
        
        // Update keys with newly uploaded PDFs (keep existing if not uploaded)
        if (uploadResult.keys.blPdf) blPdfKey = uploadResult.keys.blPdf;
        if (uploadResult.keys.leoPdf) leoPdfKey = uploadResult.keys.leoPdf;
        if (uploadResult.keys.invPlPdf) invPlPdfKey = uploadResult.keys.invPlPdf;

        // Upload multiple LEO PDFs (if provided) and store joined keys
        if (leoCandidates.length > 0) {
          const uploadedLeoKeys = [];
          for (let i = 0; i < leoCandidates.length; i += 1) {
            const singleLeoUpload = await uploadSeaExportShipmentPDFs({ leoPdf: leoCandidates[i] }, `${blNumber}_${i + 1}`);
            if (!singleLeoUpload.success) {
              toast.error(`LEO PDF ${i + 1} upload failed: ${singleLeoUpload.error}`);
              return;
            }
            if (singleLeoUpload.keys.leoPdf) {
              uploadedLeoKeys.push(singleLeoUpload.keys.leoPdf);
            }
          }

          if (uploadedLeoKeys.length > 0) {
            leoPdfKey = uploadedLeoKeys.join(' & ');
          }
        } else if (Array.isArray(updatedBooking.leoPdf)) {
          const existingLeoKeys = normalizePdfList(updatedBooking.leoPdf).filter(v => !(v instanceof File));
          leoPdfKey = existingLeoKeys.join(' & ');
        }
      }
      
      // Get current user info for editedBy
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const editedBy = currentUser.username || currentUser.name || 'Unknown';
      
      // Step 2: Prepare API payload with updated data
      if (Array.isArray(leoPdfKey)) {
        leoPdfKey = leoPdfKey.filter(Boolean).join(' & ');
      }

      const normalizedContainerNos = Array.isArray(updatedBooking.containerNo)
        ? updatedBooking.containerNo.map(v => String(v || '').trim().toUpperCase())
        : String(updatedBooking.containerNo || '')
            .split(/\s*&\s*|\s*,\s*/)
            .map(v => v.trim().toUpperCase())
            .filter(Boolean);

      const apiPayload = {
        portCode: updatedBooking.portCode,
        sbNo: updatedBooking.sbNo,
        sbDate: updatedBooking.sbDate,
        pkg: updatedBooking.pkg,
        gwt: updatedBooking.gwt,
        portOfLoading: updatedBooking.portOfLoading,
        stateOfOrigin: updatedBooking.stateOfOrigin,
        portOfDischargeCode: updatedBooking.portOfDischargeCode,
        portOfDischarge: updatedBooking.portOfDischarge,
        portOfFinalDestinationCode: updatedBooking.portOfFinalDestinationCode,
        portOfFinalDestination: updatedBooking.portOfFinalDestination,
        countryOfFinalDestination: updatedBooking.countryOfFinalDestination,
        exporterName: updatedBooking.exporterName,
        exporterAddress: updatedBooking.exporterAddress,
        gstNo: updatedBooking.gstNo,
        buyerName: updatedBooking.buyerName,
        buyerAddress: updatedBooking.buyerAddress,
        consigneeName: updatedBooking.consigneeName,
        mode: updatedBooking.mode,
        lut: updatedBooking.lut,
        fobValue: updatedBooking.fobValue,
        dbkClaim: updatedBooking.dbkClaim,
        igstAmt: updatedBooking.igstAmt,
        cessAmt: updatedBooking.cessAmt,
        igstValue: updatedBooking.igstValue,
        rodtepAmt: updatedBooking.rodtepAmt,
        rosctlAmt: updatedBooking.rosctlAmt,
        invNo: updatedBooking.invNo,
        invAmt: updatedBooking.invAmt,
        currency: updatedBooking.currency,
        leoDate: updatedBooking.leoDate,
        invoiceNoDt: updatedBooking.invoiceNoDt,
        blNumber: updatedBooking.blNumber,
        containerNo: normalizedContainerNos,
        containerQty: updatedBooking.containerQty,
        blPdf: blPdfKey,
        leoPdf: leoPdfKey,
        invPlPdf: invPlPdfKey,
        editedBy: editedBy
      };
      
      console.log('Updating sea export with payload:', apiPayload);
      
      // Step 3: Call PUT API
      const response = await apiCall(`${API_ENDPOINTS.UPDATE_SEA_EXPORT}/${updatedBooking.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiPayload)
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to update sea export shipment');
      }

      // Push GST customer data from edited sea shipment if available
      try {
        const exporterName = (updatedBooking.exporterName || '').toString().trim();
        const exporterAddress = (updatedBooking.exporterAddress || '').toString().trim();
        const normalizedGstNo = (updatedBooking.gstNo || '').toString().trim().toUpperCase();
        const numericUserId = Number(updatedBooking.customerId);

        if (exporterName && Number.isFinite(numericUserId) && numericUserId > 0 && normalizedGstNo && normalizedGstNo.length === 15) {
          apiCall(API_ENDPOINTS.INVOICE_ADD_CUSTOMER, {
            method: 'POST',
            body: JSON.stringify({
              userId: numericUserId,
              name: exporterName,
              address: exporterAddress,
              gstin: normalizedGstNo
            })
          }).catch(err => console.error('Invoice customer API error (Sea Edit):', err));
        }
      } catch (invoiceErr) {
        console.error('Invoice customer call failed (Sea Edit):', invoiceErr);
      }
      
      toast.success('Sea Export shipment updated successfully!');
      const seaCustomerUser = allUsers?.find(u => String(u.id) === String(updatedBooking.customerId));
      sendShipmentUpdateEmail({
        to_email: seaCustomerUser?.email || '',
        name: seaCustomerUser?.name || seaCustomerUser?.username || updatedBooking.exporterName || 'Customer',
        order_id: updatedBooking.blNumber || updatedBooking.id || '',
        shipment_status: 'Updated',
        tracking_id: updatedBooking.blNumber || '',
      }).catch(console.error);

      // Step 4: Refresh list from backend
      const listResponse = await apiCall(API_ENDPOINTS.GET_SEA_EXPORT, {
        method: 'GET'
      });
      
      const listData = await listResponse.json();
      
      if (listResponse.ok && listData.success) {
        const formattedShipments = (listData.data || []).map(s => ({
          id: s.id,
          portCode: s.port_code || s.portCode || '-',
          sbNo: s.sb_no || s.sbNo || '-',
          sbDate: s.sb_date || s.sbDate || '-',
          pkg: s.pkg || '-',
          gwt: s.gwt || '-',
          portOfLoading: s.port_of_loading || s.portOfLoading || '-',
          stateOfOrigin: s.state_of_origin || s.stateOfOrigin || '-',
          portOfDischargeCode: s.port_of_discharge_code || s.portOfDischargeCode || '-',
          portOfDischarge: s.port_of_discharge || s.portOfDischarge || '-',
          portOfFinalDestinationCode: s.port_of_final_destination_code || s.portOfFinalDestinationCode || '-',
          portOfFinalDestination: s.port_of_final_destination || s.portOfFinalDestination || '-',
          countryOfFinalDestination: s.country_of_final_destination || s.countryOfFinalDestination || '-',
          exporterName: s.exporter_name || s.exporterName || '-',
          exporterAddress: s.exporter_address || s.exporterAddress || '-',
          gstNo: s.gst_no || s.gstNo || '-',
          buyerName: s.buyer_name || s.buyerName || '-',
          buyerAddress: s.buyer_address || s.buyerAddress || '-',
          consigneeName: s.consignee_name || s.consigneeName || '-',
          mode: s.mode || '-',
          lut: s.lut || '-',
          fobValue: s.fob_value || s.fobValue || '-',
          dbkClaim: s.dbk_claim || s.dbkClaim || '-',
          igstAmt: s.igst_amt || s.igstAmt || '-',
          cessAmt: s.cess_amt || s.cessAmt || '-',
          igstValue: s.igst_value || s.igstValue || '-',
          rodtepAmt: s.rodtep_amt || s.rodtepAmt || '-',
          rosctlAmt: s.rosctl_amt || s.rosctlAmt || '-',
          invNo: s.inv_no || s.invNo || '-',
          invAmt: s.inv_amt || s.invAmt || '-',
          currency: s.currency || '-',
          leoDate: s.leo_date || s.leoDate || '-',
          invoiceNoDt: s.invoice_no_dt || s.invoiceNoDt || '-',
          blNumber: s.bl_number || s.blNumber || '-',
          containerNo: normalizeSeaContainerNoDisplay(s.container_no || s.containerNo),
          containerQty: s.container_qty || s.containerQty || '-',
          leoPdf: s.leo_pdf || s.leoPdf || '',
          blPdf: s.bl_pdf || s.blPdf || '',
          invPlPdf: s.inv_pl_pdf || s.invPlPdf || '',
          invoicePdf: s.invoice_pdf || s.invoicePdf || '',
          purPdf: s.pur_pdf || s.purPdf || '',
          customerId: s.user_id || s.userId || null,
          editedBy: s.edited_by || s.editedBy || '-',
          createdAt: s.created_at || s.createdAt || new Date().toISOString(),
          type: 'Sea-Export'
        }));
        
        setSeaExportBookings(formattedShipments);
      }
    } catch (error) {
      console.error('❌ Edit sea export error:', error);
      toast.error(`Failed to update shipment: ${error.message}`);
    }
  };

  const handleDeleteSeaExport = async (id) => {
    try {
      // Call DELETE API
      const response = await apiCall(`${API_ENDPOINTS.DELETE_SEA_EXPORT}/${id}`, {
        method: 'DELETE'
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success('Sea Export shipment deleted successfully!');
        
        // Refresh list from backend
        const listResponse = await apiCall(API_ENDPOINTS.GET_SEA_EXPORT, {
          method: 'GET'
        });
        
        const listData = await listResponse.json();
        
        if (listResponse.ok && listData.success) {
          const formattedShipments = (listData.data || []).map(s => ({
            id: s.id,
            portCode: s.port_code || s.portCode || '-',
            sbNo: s.sb_no || s.sbNo || '-',
            sbDate: s.sb_date || s.sbDate || '-',
            pkg: s.pkg || '-',
            gwt: s.gwt || '-',
            portOfLoading: s.port_of_loading || s.portOfLoading || '-',
            stateOfOrigin: s.state_of_origin || s.stateOfOrigin || '-',
            portOfDischargeCode: s.port_of_discharge_code || s.portOfDischargeCode || '-',
            portOfDischarge: s.port_of_discharge || s.portOfDischarge || '-',
            portOfFinalDestinationCode: s.port_of_final_destination_code || s.portOfFinalDestinationCode || '-',
            portOfFinalDestination: s.port_of_final_destination || s.portOfFinalDestination || '-',
            countryOfFinalDestination: s.country_of_final_destination || s.countryOfFinalDestination || '-',
            exporterName: s.exporter_name || s.exporterName || '-',
            exporterAddress: s.exporter_address || s.exporterAddress || '-',
            gstNo: s.gst_no || s.gstNo || '-',
            buyerName: s.buyer_name || s.buyerName || '-',
            buyerAddress: s.buyer_address || s.buyerAddress || '-',
            consigneeName: s.consignee_name || s.consigneeName || '-',
            mode: s.mode || '-',
            lut: s.lut || '-',
            fobValue: s.fob_value || s.fobValue || '-',
            dbkClaim: s.dbk_claim || s.dbkClaim || '-',
            igstAmt: s.igst_amt || s.igstAmt || '-',
            cessAmt: s.cess_amt || s.cessAmt || '-',
            igstValue: s.igst_value || s.igstValue || '-',
            rodtepAmt: s.rodtep_amt || s.rodtepAmt || '-',
            rosctlAmt: s.rosctl_amt || s.rosctlAmt || '-',
            invNo: s.inv_no || s.invNo || '-',
            invAmt: s.inv_amt || s.invAmt || '-',
            currency: s.currency || '-',
            leoDate: s.leo_date || s.leoDate || '-',
            invoiceNoDt: s.invoice_no_dt || s.invoiceNoDt || '-',
            blNumber: s.bl_number || s.blNumber || '-',
            containerNo: normalizeSeaContainerNoDisplay(s.container_no || s.containerNo),
            containerQty: s.container_qty || s.containerQty || '-',
            leoPdf: s.leo_pdf || s.leoPdf || '',
            blPdf: s.bl_pdf || s.blPdf || '',
            invPlPdf: s.inv_pl_pdf || s.invPlPdf || '',
            invoicePdf: s.invoice_pdf || s.invoicePdf || '',
            purPdf: s.pur_pdf || s.purPdf || '',
            customerId: s.user_id || s.userId || null,
            editedBy: s.edited_by || s.editedBy || '-',
            createdAt: s.created_at || s.createdAt || new Date().toISOString(),
            type: 'Sea-Export'
          }));
          
          setSeaExportBookings(formattedShipments);
        }
      } else {
        toast.error(result.message || 'Failed to delete sea export shipment');
      }
    } catch (error) {
      console.error('❌ Delete sea export error:', error);
      toast.error(`Failed to delete shipment: ${error.message}`);
    }
  };

  const requiredLeoCount = scrapedDataForSubmit?.cleanedData?.sbNumbers && Array.isArray(scrapedDataForSubmit.cleanedData.sbNumbers)
    ? scrapedDataForSubmit.cleanedData.sbNumbers.filter(Boolean).length
    : 0;
  const uploadedLeoCount = Array.isArray(submitFormData.leo) ? submitFormData.leo.length : (submitFormData.leo ? 1 : 0);
  const hasCompleteLeoSet = requiredLeoCount > 0 ? uploadedLeoCount === requiredLeoCount : uploadedLeoCount > 0;

  const normalizeNumberValue = (value) => (value ?? '').toString().replace(/,/g, '').trim();
  const hasLeoUpload = Array.isArray(submitFormData.leo) ? submitFormData.leo.length > 0 : !!submitFormData.leo;
  const hasExistingLeo = !!(editingShipment?.leo || editingShipment?.leoPdf || editingShipment?.leoPdfKey);
  const leoRequirementMet = isEditMode ? (hasLeoUpload || hasExistingLeo) : !!fetchedLEOData;

  const hasPdfChanges = !!submitFormData.awb || !!submitFormData.inv || hasLeoUpload;
  const hasFieldChanges = isEditMode && editingShipment && (
    (submitFormData.customer_name || '') !== (editingShipment.customerName || editingShipment.username || '') ||
    normalizeNumberValue(submitFormData.DBK || '0') !== normalizeNumberValue(editingShipment.dbk ?? editingShipment.DBK ?? editingShipment.dbkClaim ?? '0') ||
    normalizeNumberValue(submitFormData.Rodtep || '0') !== normalizeNumberValue(editingShipment.rodtep ?? editingShipment.Rodtep ?? editingShipment.rodtepAmt ?? '0') ||
    normalizeNumberValue(submitFormData.Rosctl || '0') !== normalizeNumberValue(editingShipment.rosctl ?? editingShipment.Rosctl ?? editingShipment.rosctlAmt ?? '0') ||
    normalizeNumberValue(submitFormData.IGST || '0') !== normalizeNumberValue(editingShipment.igst ?? editingShipment.IGST ?? editingShipment.igstAmt ?? '0')
  );

  const canSubmitForm = isEditMode
    ? (leoRequirementMet && (hasPdfChanges || hasFieldChanges))
    : !!fetchedLEOData;

  const navSections = [
    { title: 'Export', items: [
      { id: 'Air-Export', label: 'Air Export', icon: Plane },
      { id: 'Sea-Export', label: 'Sea Export', icon: Ship },
    ] },
    { title: 'Import', items: [
      { id: 'Air-Import', label: 'Air Import', icon: Plane },
      { id: 'Sea-Import', label: 'Sea Import', icon: Ship },
    ] },
    { title: 'Approvals', items: [
      { id: 'pendingApproval', label: 'Pending Approval', icon: Inbox, count: pendingApprovalCount },
    ] },
  ];

  // The topbar box searches whatever tab is open; a tab with nothing to
  // search gets no box rather than a dead one.
  const tabSearch = {
    'Air-Export': { value: consoleSearch, set: setConsoleSearch, hint: 'Search AWB, S/B, user, consignee…' },
    'Sea-Export': { value: consoleSearch, set: setConsoleSearch, hint: 'Search B/L, S/B, exporter, port…' },
  }[activeTab] || null;

  return (
    <AdminShell
      sections={navSections}
      activeId={activeTab}
      onSelect={setActiveTab}
      role="Employee"
      search={tabSearch?.value ?? ''}
      onSearch={tabSearch?.set}
      searchPlaceholder={tabSearch?.hint}
    >
      <div className="max-w-[1600px] mx-auto">
        {activeTab === 'pendingApproval' && (
          <PendingApprovalQueue
            eyebrow="Employee · Automation"
            onApproved={() => refetchShipments()}
            onCountChange={setPendingApprovalCount}
          />
        )}

        {/* Coming Soon for other tabs */}
        {activeTab === 'pendingApproval' ? null
          : activeTab !== 'Air-Export' && activeTab !== 'Sea-Export' ? (
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="mb-6">
                {activeTab === 'Air-Import' && <Plane size={80} className="mx-auto text-blue-500 dark:text-blue-400" />}
                {activeTab === 'Sea-Import' && <Ship size={80} className="mx-auto text-teal-500 dark:text-teal-400" />}
              </div>
              <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-200 mb-4">
                Coming Soon
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-lg">
                {activeTab} feature is under development
              </p>
            </div>
          </div>
        ) : activeTab === 'Sea-Export' ? (
          <>
            <PageHead
              eyebrow="Employee · Sea export"
              title="Sea Export"
              badge={<Pill tone="emerald" dot>{seaExportBookings.length} bookings</Pill>}
              subtitle="Raise a sea export booking and keep its shipping bill details up to date."
            />
            <SeaExportForm 
              onClose={handleCloseSeaExportForm} 
              onSubmit={handleSeaExportSubmit}
              customers={customers}
              existingBookings={seaExportBookings}
              onCustomerChange={setSeaSelectedCustomerId}
            />

            {/* Sea Export Table */}
            <SeaExportTable 
              shipments={filteredSeaExportBookings}
              onEdit={handleEditSeaExport}
              onDelete={handleDeleteSeaExport}
              loading={loadingSeaExport}
            />
          </>
        ) : (
          <>
        <PageHead
          eyebrow="Employee · Air export"
          title="Air Export"
          badge={<Pill tone="emerald" dot>{shipments.length} shipments</Pill>}
          subtitle="Fetch a consignment from customs, file its documents and send it for approval."
        />

        <>
        {/* Fetch Data Section */}
        <Panel className="px-4 sm:px-5 py-5 mb-5">
          <div className="mb-4">
            <p className="text-[15px] font-bold text-slate-900 dark:text-white">Fetch shipment data</p>
            <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5">
              Pull a consignment from customs by its AWB and file it against a user.
            </p>
          </div>
          <form onSubmit={handleFetchData} className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
            <div>
              <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">Select user <span className="text-rose-500">*</span></label>
              <select
                value={fetchForm.customer}
                onChange={(e) => {
                  const value = e.target.value;
                  const selected = customers.find(c => String(c.id) === String(value));
                  setFetchForm({ ...fetchForm, customer: value });
                  setSelectedCustomer(selected?.name || '');
                }}
                className="ql-select h-11 w-full rounded-xl px-3.5 text-sm bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700/70 text-slate-800 dark:text-slate-100 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10"
                required
              >
                <option value="">Select user</option>
                {customers.map((customer, index) => (
                  <option key={`${customer.id}-${customer.name || customer.username || 'customer'}-${index}`} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">AWB number <span className="text-rose-500">*</span></label>
              <input
                type="text"
                value={fetchForm.awbNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                  
                  // DCSC prefix codes (first 3 digits)
                  const dcscPrefixes = [
                    '098', '106', '126', '745', '047', '064', '081', '281', '096', '199',
                    '777', '580', '843', '205', '485', '628', '786', '068', '997', '620',
                    '301', '159', '908', '114', '176', '230', '729', '141', '044', '574',
                    '202', '083', '124', '749', '996', '417', '607', '105', '017',
                    '879', '747', '501', '384', '805', '026', '828', '861', '807', '833',
                    '859', '020', '537', '986', '599', '478', '079', '339', '319', '921',
                    '618', '656', '731', '112', '549', '672', '413', '775', '603', '550',
                    '566', '880', '999', '724', '082', '900', '940', '217', '258', '572',
                    '380', '978', '738', '228', '860', '077'
                  ];
                  
                  // Auto-select shipment type based on first 3 digits when AWB is 3+ digits
                  let autoSelectBox2 = fetchForm.selectBox2;
                  if (value.length >= 3) {
                    const prefix = value.substring(0, 3);
                    autoSelectBox2 = dcscPrefixes.includes(prefix) ? 'DCSC' : 'GMR Aero';
                  }
                  
                  // Check if AWB exists in table when 11 digits entered
                  if (value.length === 11) {
                    const existingShipment = shipments.find(s => s.awbNumber === value);
                    if (existingShipment) {
                      // Set duplicate AWB info immediately
                      setDuplicateAwbShipment(existingShipment);
                      const ownerName = existingShipment.username || 'Unknown User';
                      setDuplicateAwbMessage(
                        `Entered AWB ${value} already assigned to ${ownerName}. Users switch to ${ownerName} for updates.`
                      );
                      
                      // Find customer by username field
                      const matchingCustomer = customers.find(c => 
                        c.name === existingShipment.username
                      );
                      
                      // Update form with customer (keep auto-selected shipment type)
                      setFetchForm({
                        ...fetchForm,
                        awbNumber: value,
                        customer: matchingCustomer ? String(matchingCustomer.id) : fetchForm.customer,
                        selectBox2: autoSelectBox2
                      });
                    } else {
                      // Clear duplicate message if AWB doesn't exist
                      setDuplicateAwbMessage('');
                      setDuplicateAwbShipment(null);
                      setFetchForm({...fetchForm, awbNumber: value, selectBox2: autoSelectBox2});
                    }
                  } else {
                    // Clear duplicate message when user changes input to less than 11 digits
                    setDuplicateAwbMessage('');
                    setDuplicateAwbShipment(null);
                    setFetchForm({...fetchForm, awbNumber: value, selectBox2: autoSelectBox2});
                  }
                }}
                className="h-11 w-full rounded-xl px-3.5 text-sm bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700/70 text-slate-800 dark:text-slate-100 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 font-mono"
                placeholder="11 digit AWB number"
                required
                maxLength="11"
              />
              <div className="h-5 mt-1">
                {fetchForm.awbNumber && fetchForm.awbNumber.length < 11 && (
                  <p className="text-[11.5px] font-semibold text-rose-500">AWB number must be 11 digits</p>
                )}
                {duplicateAwbMessage && (
                  <p className="text-[11.5px] font-semibold text-rose-500">{duplicateAwbMessage}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">&nbsp;</label>
              <button
                type="submit"
                disabled={
                  isFetchingData ||
                  !fetchForm.customer ||
                  !fetchForm.selectBox2 ||
                  fetchForm.awbNumber.length < 11
                }
                className={`${btn.primary} w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <Search size={16} />
                {isFetchingData ? 'Fetching...' : (duplicateAwbShipment ? 'Update Data' : 'Fetch Data')}
              </button>
              {isFetchingData ? (
                <div className="mt-2">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-blue-100 dark:bg-slate-700">
                    <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500 animate-[fetchLinearLoad_1.2s_ease-in-out_infinite]"></div>
                  </div>
                  <style>{`
                    @keyframes fetchLinearLoad {
                      0% {
                        transform: translateX(-120%);
                      }
                      100% {
                        transform: translateX(320%);
                      }
                    }
                  `}</style>
                </div>
              ) : (
                <div className="h-5 mt-1"></div>
              )}
            </div>
          </form>
          
          {/* Error Message */}
          {fetchError && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">{fetchError}</p>
            </div>
          )}
        </Panel>

        {/* Shipments Table */}
        <Panel className="overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-4 border-b border-slate-100 dark:border-slate-800">
            <div className="flex items-center gap-2.5">
              <p className="text-[15px] font-bold text-slate-900 dark:text-white">All shipments</p>
              <Pill tone="slate">{activeTab === 'Sea-Export' ? seaExportBookings.length : filteredShipments.length}</Pill>
            </div>
            <button
              onClick={() => {
                const headers = ['AWB Number', 'Username', 'Customer Name', 'SB Number', 'SB Date', 'City Code', 'Dest City', 'Dest Country', 'Flight No', 'Flight Date', 'Total Pkt', 'Weight', 'CH Weight', 'Amount', 'Activity', 'FOB', 'DBK', 'Rodtep', 'Rosctl', 'IGST', 'AWB', 'LEO', 'INV/PL'];

                const formatAwbForCsv = (awb) => {
                  if (awb === null || awb === undefined || awb === '') return '';
                  const str = String(awb);
                  return str.startsWith('0') ? `="${str}"` : str;
                };


                const csvRows = filteredShipments.map(s => [
                  formatAwbForCsv(s.awbNumber || ''),
                  s.username || '',
                  s.customerName || '',
                  s.sbNumber || '',
                  s.sbDate || '',
                  s.cityCode || '',
                  s.destCity || '',
                  s.destCountry || '',
                  s.flightNo || '',
                  s.flightDate || '',
                  s.totalPkt || '',
                  s.weight || '',
                  s.chWeight || '',
                  s.amount || '',
                  s.activity || '',
                  s.fob || '',
                  s.dbk || '',
                  s.rodtep || '',
                  s.rosctl || '',
                  s.igst || '',
                  s.awbPdf ? 'Yes' : 'No',
                  s.leo ? 'Yes' : 'No',
                  s.invpl ? 'Yes' : 'No'
                ]);

                const csvContent = [headers.join(','), ...csvRows.map(row => row.map(escapeCsv).join(','))].join('\n');
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `shipments_${new Date().toISOString().split('T')[0]}.csv`;
                link.click();
              }}
              className={btn.green}
            >
              <Download size={16} /> Export CSV
            </button>
          </div>
          <ShipmentsTable 
            shipments={filteredShipments} 
            userRole="employee" 
            onEdit={handleEditShipment}
            onDelete={handleDeleteShipment}
          />
        </Panel>
        </>
          </>
        )}
      </div>

      {/* Submit Form Modal */}
      {showSubmitForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl w-full max-h-[95vh] overflow-y-auto"
            style={{
              maxWidth: 'min(95vw, 700px)',
              scrollbarWidth: 'thin',
              scrollbarColor: '#3b82f6 #e5e7eb'
            }}
          >
            <div className="sticky top-0 bg-blue-600 p-4 sm:p-6 rounded-t-lg z-10">
              <div className="flex items-start sm:items-center justify-between gap-3">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white">{isEditMode ? 'Edit Shipment Details' : 'Submit Shipment Details'}</h2>
                <button
                  onClick={handleCancelSubmitForm}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>
              {scrapedDataForSubmit && (
                <p className="text-sm text-blue-100 mt-2">
                  AWB: {scrapedDataForSubmit.cleanedData.awbNumber}
                </p>
              )}
            </div>

            <form onSubmit={handleSubmitFormSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-5">
              {/* PDF Upload Section - AT THE TOP */}
              <div className="border border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20 rounded-lg p-3 sm:p-4">
                <h3 className="text-sm sm:text-base font-semibold text-green-900 dark:text-green-300 mb-3 sm:mb-4">Document Uploads</h3>
                
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
                  {/* AWB Copy */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      AWB Copy
                    </label>
                    
                    {isValidatingAwbPdf ? (
                      <div className="p-4 border-2 border-dashed border-blue-400 dark:border-blue-500 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex flex-col items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">Verifying AWB PDF...</span>
                      </div>
                    ) : (isEditMode && editingShipment?.awbPdf && !submitFormData.awb) ? (
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/25 border-2 border-blue-200 dark:border-blue-700/50 rounded-lg">
                        <div className="flex flex-col items-stretch gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              // On the CDN, like the LEO and invoice PDFs below.
                              window.open(getCloudFrontUrl(editingShipment.awbPdf), '_blank');
                            }}
                            className="w-full px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700 rounded-md transition flex items-center justify-center gap-2"
                          >
                            <FileText size={18} className="flex-shrink-0" />
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePdfDeleteClick('awb', 'editing')}
                            className="w-full px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/60 rounded-md transition flex items-center justify-center gap-1"
                            title="Remove"
                          >
                            <X size={18} className="flex-shrink-0" />
                            Clear
                          </button>
                        </div>
                      </div>
                    ) : submitFormData.awb ? (
                      <div className="p-3 bg-green-50 dark:bg-green-900/25 border-2 border-green-200 dark:border-green-700/50 rounded-lg">
                        <div className="flex flex-col items-stretch gap-2 rounded-md border border-green-200/70 dark:border-green-700/40 p-2 bg-white/70 dark:bg-black/10">
                          <button
                            type="button"
                            onClick={() => {
                              const fileURL = URL.createObjectURL(submitFormData.awb);
                              window.open(fileURL, '_blank');
                            }}
                            className="w-full px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800 hover:bg-green-200 dark:hover:bg-green-700 rounded-md transition flex items-center justify-center gap-2"
                          >
                            <FileText size={18} className="flex-shrink-0" />
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePdfDeleteClick('awb', 'submit')}
                            className="w-full px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/60 rounded-md transition flex items-center justify-center gap-1"
                            title="Remove"
                          >
                            <X size={18} className="flex-shrink-0" />
                            Clear
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition">
                        <Upload size={20} className="text-slate-400" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">Upload PDF</span>
                        <input
                          type="file"
                          name="awb"
                          accept=".pdf"
                          onChange={handleSubmitFormChange}
                          className="hidden"
                        />
                      </label>
                    )}
                    
                    {awbPdfError && (
                      <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md">
                        <p className="text-xs text-red-600 dark:text-red-400 font-medium">{awbPdfError}</p>
                      </div>
                    )}
                  </div>

                  {/* LEO */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      LEO <span className="text-red-500">*</span> <span className="text-xs text-red-500">(Required)</span>
                    </label>

                    {(isEditMode && editingShipment?.leo && (!submitFormData.leo || submitFormData.leo.length === 0)) ? (
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/25 border-2 border-blue-200 dark:border-blue-700/50 rounded-lg">
                        <div className="space-y-2">
                          {normalizePdfList(editingShipment.leoPdfKey || editingShipment.leoPdf || editingShipment.leo).map((leoItem, idx) => {
                            const url = leoItem.startsWith('http') ? leoItem : getCloudFrontUrl(leoItem);
                            return (
                              <div key={idx} className="flex flex-col items-stretch gap-2 rounded-md border border-blue-200/70 dark:border-blue-700/40 p-2 bg-white/70 dark:bg-black/10">
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-full px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700 rounded-md transition flex items-center justify-center gap-2"
                                >
                                  <FileText size={18} className="flex-shrink-0" />
                                  View LEO {idx + 1}
                                </a>
                              </div>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          onClick={() => handlePdfDeleteClick('leo', 'editing')}
                          className="mt-2 w-full px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/60 rounded-md transition flex items-center justify-center gap-1"
                          title="Remove"
                        >
                          <X size={18} className="flex-shrink-0" />
                          Clear
                        </button>
                      </div>
                    ) : Array.isArray(submitFormData.leo) && submitFormData.leo.length > 0 && hasCompleteLeoSet ? (
                      <div className="p-3 bg-green-50 dark:bg-green-900/25 border-2 border-green-200 dark:border-green-700/50 rounded-lg">
                        <div className="space-y-2">
                          {submitFormData.leo.map((file, index) => (
                              <div key={`${file.name}-${index}`} className="flex flex-col items-stretch gap-2 rounded-md border border-green-200/70 dark:border-green-700/40 p-2 bg-white/70 dark:bg-black/10">
                              <button
                                type="button"
                                onClick={() => {
                                  const fileURL = URL.createObjectURL(file);
                                  window.open(fileURL, '_blank');
                                }}
                                className="w-full px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800 hover:bg-green-200 dark:hover:bg-green-700 rounded-md transition flex items-center justify-center gap-2"
                              >
                                <FileText size={18} className="flex-shrink-0" />
                                View {index + 1}
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => handlePdfDeleteClick('leo', 'submit')}
                          className="mt-2 w-full px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/60 rounded-md transition flex items-center justify-center gap-1"
                          title="Remove"
                        >
                          <X size={18} className="flex-shrink-0" />
                          Clear All
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition">
                        <Upload size={20} className="text-slate-400" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">Upload PDFs</span>
                        <input
                          type="file"
                          name="leo"
                          accept=".pdf"
                          multiple
                          required
                          onChange={handleSubmitFormChange}
                          className="hidden"
                        />
                      </label>
                    )}

                    {scrapedDataForSubmit?.cleanedData?.sbNumbers && Array.isArray(scrapedDataForSubmit.cleanedData.sbNumbers) && scrapedDataForSubmit.cleanedData.sbNumbers.length > 0 ? (
                      <div className="mt-2 text-[11px] leading-4 text-slate-600 dark:text-slate-400">
                        <span className="block sm:hidden break-words">
                          Required LEO PDFs: {scrapedDataForSubmit.cleanedData.sbNumbers.length} | SB: {scrapedDataForSubmit.cleanedData.sbNumbers.filter(Boolean).join(' & ')} | One PDF per SB (exact match).
                        </span>
                        <div className="hidden sm:block max-w-[260px]">
                          <span className="block">Required LEO PDFs: {scrapedDataForSubmit.cleanedData.sbNumbers.length}</span>
                          <span className="block">SB: {scrapedDataForSubmit.cleanedData.sbNumbers.filter(Boolean).join(' & ')}</span>
                          <span className="block">One PDF per SB (exact match).</span>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 text-[11px] leading-4 text-amber-600 dark:text-amber-400 max-w-[260px]">
                        SB numbers not found. Fetch AWB details first.
                      </div>
                    )}
                  </div>

                  {/* INV */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      INV (Invoice PDFs)
                    </label>
                    {(isEditMode && editingShipment?.inv && normalizePdfList(editingShipment.invPdf || editingShipment.inv).length > 0 && !submitFormData.inv) ? (
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/25 border-2 border-blue-200 dark:border-blue-700/50 rounded-lg">
                        <div className="space-y-2">
                          {normalizePdfList(editingShipment.invPdf || editingShipment.inv).map((invItem, idx) => {
                            const url = invItem.startsWith('http') ? invItem : getCloudFrontUrl(invItem);
                            return (
                              <div key={idx} className="flex flex-col items-stretch gap-2 rounded-md border border-blue-200/70 dark:border-blue-700/40 p-2 bg-white/70 dark:bg-black/10">
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-full px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700 rounded-md transition flex items-center justify-center gap-2"
                                >
                                  <FileText size={18} className="flex-shrink-0" />
                                  View INV {idx + 1}
                                </a>
                              </div>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          onClick={() => handlePdfDeleteClick('inv', 'editing')}
                          className="mt-2 w-full px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/60 rounded-md transition flex items-center justify-center gap-1"
                          title="Remove"
                        >
                          <X size={18} className="flex-shrink-0" />
                          Clear
                        </button>
                      </div>
                    ) : Array.isArray(submitFormData.inv) && submitFormData.inv.length > 0 ? (
                      <div className="p-3 bg-green-50 dark:bg-green-900/25 border-2 border-green-200 dark:border-green-700/50 rounded-lg">
                        <div className="space-y-2">
                          {submitFormData.inv.map((file, idx) => (
                            <div key={idx} className="flex flex-col items-stretch gap-2 rounded-md border border-green-200/70 dark:border-green-700/40 p-2 bg-white/70 dark:bg-black/10">
                              <button
                                type="button"
                                onClick={() => {
                                  const fileURL = URL.createObjectURL(file);
                                  window.open(fileURL, '_blank');
                                }}
                                className="w-full px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-800 hover:bg-green-200 dark:hover:bg-green-700 rounded-md transition flex items-center justify-center gap-2"
                              >
                                <FileText size={18} className="flex-shrink-0" /> View {idx + 1}
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSubmitFormData(prev => ({
                              ...prev,
                              inv: []
                            }));
                          }}
                          className="mt-2 w-full px-3 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/60 rounded-md transition flex items-center justify-center gap-1"
                          title="Remove"
                        >
                          <X size={18} className="flex-shrink-0" />
                          Clear All
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition">
                        <Upload size={20} className="text-slate-400" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">Upload PDF(s)</span>
                        <input
                          type="file"
                          name="inv"
                          accept=".pdf"
                          multiple
                          onChange={handleSubmitFormChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Combined Extracted Data Section */}
              {(isLoadingLEOData || fetchedLEOData || (scrapedDataForSubmit && scrapedDataForSubmit.cleanedData)) && (
                <div className="border border-slate-300 dark:border-slate-600 rounded-lg p-4 mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                      Shipment Data
                    </h3>
                    {isLoadingLEOData && (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-slate-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-sm text-slate-700 dark:text-slate-300">Processing...</span>
                      </div>
                    )}
                  </div>
                  
                  {leoFetchError && (
                    <div className="p-3 bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700 rounded-md mb-4 text-red-700 dark:text-red-300 text-sm">
                      {leoFetchError}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">AWB No.</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.cleanedData?.awbNumber || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Origin Port Code</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.cleanedData?.portCode || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">City Code</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.destinationData?.dest_code || scrapedDataForSubmit?.cleanedData?.destCode || fetchedLEOData?.portOfDischargeCode || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Dest. Country</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.destinationData?.dest_country || scrapedDataForSubmit?.cleanedData?.destCountry || fetchedLEOData?.countryOfFinalDestination || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Dest. City</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.destinationData?.dest_city || scrapedDataForSubmit?.cleanedData?.destCity || (() => { const raw = fetchedLEOData?.portOfDischarge || ''; const m = raw.match(/\(([^)]+)\)/); return m ? m[1].trim() : raw; })() || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Total Pkt</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.cleanedData?.numberOfPackages || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">CH Weight (kg)</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.cleanedData?.chargeableWeight || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">S/B Date</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.cleanedData?.sbDate ? formatDateToDDMMYYYY(scrapedDataForSubmit.cleanedData.sbDate) : (formatDateArray(scrapedDataForSubmit?.cleanedData?.sbDates) || <span className="text-slate-400 dark:text-slate-500">-</span>)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">S/B No.</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.cleanedData?.sbNumber || (scrapedDataForSubmit?.cleanedData?.sbNumbers && Array.isArray(scrapedDataForSubmit.cleanedData.sbNumbers) && scrapedDataForSubmit.cleanedData.sbNumbers.filter(Boolean).length > 0 ? scrapedDataForSubmit.cleanedData.sbNumbers.join(' & ') : <span className="text-slate-400 dark:text-slate-500">-</span>)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Consignee Name</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.cleanedData?.consigneeName || fetchedLEOData?.consigneeName || fetchedLEOData?.customerName || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Buyer Name</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.cleanedData?.buyerName || fetchedLEOData?.buyerName || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">FOB ₹</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.cleanedData?.sbFobValues && Array.isArray(scrapedDataForSubmit.cleanedData.sbFobValues) && scrapedDataForSubmit.cleanedData.sbFobValues.filter(Boolean).length > 0 ? scrapedDataForSubmit.cleanedData.sbFobValues.join(' & ') : <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">IGST ₹</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {fetchedLEOData?.IGST || submitFormData.IGST || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">DBK ₹</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {fetchedLEOData?.DBK || submitFormData.DBK || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Rodtep ₹</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {fetchedLEOData?.Rodtep || submitFormData.Rodtep || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Rosctl ₹</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {fetchedLEOData?.Rosctl || submitFormData.Rosctl || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Weight (kg)</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.cleanedData?.grossWeight || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">FC Value</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {fetchedLEOData?.invoiceAmount || (Array.isArray(fetchedLEOData?.fcValues) ? fetchedLEOData.fcValues.join(' & ') : '') || scrapedDataForSubmit?.cleanedData?.fcValue || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">FC Type</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {fetchedLEOData?.currency || scrapedDataForSubmit?.cleanedData?.fcType || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Flight Date</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.cleanedData?.flightDate ? formatDateToDDMMYYYY(scrapedDataForSubmit.cleanedData.flightDate) : (formatDateArray(scrapedDataForSubmit?.cleanedData?.flightDates) || <span className="text-slate-400 dark:text-slate-500">-</span>)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Flight No.</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.cleanedData?.flightNo || (scrapedDataForSubmit?.cleanedData?.flightNumbers && Array.isArray(scrapedDataForSubmit.cleanedData.flightNumbers) && scrapedDataForSubmit.cleanedData.flightNumbers.filter(Boolean).length > 0 ? scrapedDataForSubmit.cleanedData.flightNumbers.join(' & ') : <span className="text-slate-400 dark:text-slate-500">-</span>)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Activity</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.cleanedData?.paymentActivities && Array.isArray(scrapedDataForSubmit.cleanedData.paymentActivities) && scrapedDataForSubmit.cleanedData.paymentActivities.filter(Boolean).length > 0 ? scrapedDataForSubmit.cleanedData.paymentActivities.join(' & ') : <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Amount ₹</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {scrapedDataForSubmit?.cleanedData?.paymentAmounts && Array.isArray(scrapedDataForSubmit.cleanedData.paymentAmounts) && scrapedDataForSubmit.cleanedData.paymentAmounts.filter(Boolean).length > 0 ? scrapedDataForSubmit.cleanedData.paymentAmounts.join(' & ') : <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">FC Rate</label>
                      <div className="px-4 py-2.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
                        {fetchedLEOData?.exchangeRate || scrapedDataForSubmit?.cleanedData?.fcRate || <span className="text-slate-400 dark:text-slate-500">-</span>}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="pt-6 border-t-2 border-slate-200 dark:border-slate-700 mt-6 space-y-4">
                {isSubmitting && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm text-blue-600 dark:text-blue-400 font-medium">
                      <span>{isEditMode ? 'Updating shipment...' : 'Submitting shipment...'}</span>
                      <span>Please wait</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-blue-100 dark:bg-slate-700">
                      <div className="h-full w-1/3 rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500 animate-[shipmentLinearLoad_1.2s_ease-in-out_infinite]"></div>
                    </div>
                    <style>{`
                      @keyframes shipmentLinearLoad {
                        0% {
                          transform: translateX(-120%);
                        }
                        100% {
                          transform: translateX(320%);
                        }
                      }
                    `}</style>
                  </div>
                )}
                <div className="flex gap-4">
                <button
                  type="button"
                  onClick={handleCancelSubmitForm}
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3.5 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-xl font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 transition-all duration-200 hover:border-slate-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canSubmitForm || isSubmitting}
                  className={`flex-1 px-6 py-3.5 rounded-xl font-semibold shadow-lg transition-all duration-200 flex items-center justify-center gap-2 transform ${
                    (canSubmitForm && !isSubmitting)
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white hover:shadow-xl hover:-translate-y-0.5 cursor-pointer' 
                      : 'bg-slate-400 dark:bg-slate-600 text-slate-600 dark:text-slate-400 cursor-not-allowed opacity-60'
                  }`}
                  title={!canSubmitForm ? (isEditMode ? 'Make a change and ensure LEO PDF is available' : 'LEO PDF data is required') : ''}
                >
                  {isSubmitting ? (
                    <>{isEditMode ? 'Updating...' : 'Submitting...'}</>
                  ) : (
                    <>
                      <Save size={20} />
                      {isEditMode ? 'Update' : 'Submit'}
                    </>
                  )}
                </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* PDF Delete Confirmation Modal */}
      {showPdfDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                  <Trash2 size={24} className="text-orange-600 dark:text-orange-400" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                  Remove {pdfToDelete?.pdfType?.toUpperCase()} File?
                </h3>
              </div>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                <span className="block mb-2">You are about to remove this PDF file from the shipment.</span>
                <span className="text-sm text-orange-600 dark:text-orange-400 block">
                  ⚠️ This action cannot be undone.
                </span>
              </p>
              <div className="flex gap-3">
                <button
                  onClick={cancelPdfDelete}
                  className="flex-1 px-4 py-2.5 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-100 dark:hover:bg-slate-700 transition"
                >
                  Keep It
                </button>
                <button
                  onClick={confirmPdfDelete}
                  className="flex-1 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition shadow-md hover:shadow-lg"
                >
                  Remove File
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-2xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
                Delete Shipment
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                Are you sure you want to delete shipment <span className="font-semibold text-slate-900 dark:text-white">{shipmentToDelete?.awbNumber}</span>?
                <br />
                <span className="text-sm text-red-600 mt-2 block">This action cannot be undone.</span>
              </p>
              <div className="flex gap-3">
                <button
                  onClick={cancelDeleteShipment}
                  className="flex-1 px-4 py-2.5 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg font-medium hover:bg-slate-100 dark:hover:bg-slate-700 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteShipment}
                  className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition shadow-md hover:shadow-lg"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
};

export default EmployeeDashboard;
